/* Orrery — page behaviour (assets/orrery.js, moved from orrery3.html)
   - smooth scroll from the hero button to the form
   - gift toggle shows a short note
   - format selection updates the final call-to-action
   - FAQ accordion
   - form validation and draft saving; the order is a Shopify product form
     that adds the chosen variant to the cart with the birth details as line
     item properties, then goes to checkout (Shopify owns post-purchase)
   - the live chart, the buy path beneath it, the free reading by email, the
     place lookup, the reading dialog, the loupe
   Configuration comes from window.ORRERY, written by the section from its settings. */

(function () {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const form = $('#order');

  /* ---- services, from the section settings (see mailer/README.md).
     With sendChartUrl empty the reading offer runs in demo mode: nothing is
     emailed, and the link it would have sent is printed to the console. Unsigned
     links are never accepted: only a link signed by the mailer's private key
     opens a chart from the email. */
  const CONFIG = window.ORRERY || {};
  const SERVICES = Object.assign({ sendChartUrl: '', linkPublicKey: null, chartApi: '' }, CONFIG.services || {}, { acceptUnsignedLinks: false });
  const CURRENCY = CONFIG.currency || 'USD';
  /* A given chart (the permanent page at /c/<id>, served by the Worker): the
     birth details are known and fixed, the form is hidden, and the chart is
     drawn whole — including a chart with no birth time, which the Worker casts
     for sunrise with solar houses. Nothing is staged and nothing is sold here
     except by the one link to the product page. */
  const GIVEN = CONFIG.chart || null;
  const cta = $('.cta');
  const DRAFT_KEY = 'orrery.draft';
  /* Whether this device has already given an address for the reading. Read here,
     at the top, because syncChartState consults it long before the reading block
     below is reached. */
  const UNLOCK_KEY = 'orrery.reading';
  let unlocked = false;
  try { unlocked = !!localStorage.getItem(UNLOCK_KEY); } catch (_) {}

  /* ----------------------------------------------------------- scroll
     Horizon scrolls its .page-wrapper rather than the window on wide screens,
     so every scroll read and write goes through whichever is scrolling. */
  const headerEl = $('#header-component');
  const pageWrapper = $('.page-wrapper');
  function scrollRoot() {
    return pageWrapper && /auto|scroll/.test(getComputedStyle(pageWrapper).overflowY) ? pageWrapper : null;
  }
  function scrollToEl(el, extra) {
    const offset = (headerEl ? headerEl.offsetHeight : 0) + (extra || 16);
    const root = scrollRoot();
    const y = el.getBoundingClientRect().top + (root ? root.scrollTop : window.scrollY) - offset;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (root || window).scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });
  }
  $$('[data-scroll]').forEach(link => {
    link.addEventListener('click', e => {
      // the header button carries the product's path as well as the hash
      const href = link.getAttribute('href') || '';
      const path = href.split('#')[0];
      if (path && path !== location.pathname) return;
      const target = document.getElementById(href.split('#')[1] || '');
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
      // only pull focus into a field when we have actually landed on the fields
      if (target.id === 'details') {
        const first = $('#name');
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 450);
      }
    });
  });

  /* ----------------------------------------------------------- who it is for
     The name field is the one the gift tabs rename: it asks for the name that
     goes on the print, and whose that is changes with the answer. Label and
     placeholder only — the value is never touched, so switching tabs after
     typing a name keeps it. */
  const giftNote = $('#gift-note');
  const nameLabel = $('label[for="name"]');
  const nameInput = $('#name');
  function renderGift() {
    /* The gift tabs are the storefront's; the permanent chart page (worker/
       src/page.js) renders the same reading with no order form behind it. This
       threw there on every load — before the wheel or the reading had been
       drawn — so that page showed its header and nothing else. */
    const tabs = form.elements['properties[Gift]'];
    if (!tabs) return;
    const gift = tabs.value === 'Yes';
    if (giftNote) giftNote.hidden = !gift;
    if (nameLabel) nameLabel.textContent = gift ? 'Their name for the print' : 'Your name for the print';
    if (nameInput) nameInput.placeholder = gift ? 'Their name' : 'Your name';
  }
  $$('input[name="properties[Gift]"]').forEach(r => r.addEventListener('change', renderGift));
  renderGift();

  /* ----------------------------------------------------------- format → price, CTA, bar */
  const ctaBlurb = $('[data-cta-blurb]');

  const chosenFormat = () => $('input[name="id"]:checked');
  const formatKey = f => (f && f.dataset.format) || '';

  function currentPrice() {
    const f = chosenFormat();
    if (!f) return 0;
    return Number(f.dataset.price);
  }
  function currentTitle() {
    const f = chosenFormat();
    return f.dataset.title;
  }

  function syncFormat() {
    const chosen = chosenFormat();
    if (!chosen) return;

    $$('[data-cta-title]').forEach(el => { el.textContent = currentTitle(); });
    $$('[data-cta-price]').forEach(el => { el.textContent = '$' + currentPrice() + ' ' + CURRENCY; });
    if (ctaBlurb) ctaBlurb.textContent = chosen.dataset.blurb;

    // the one saving, stated in the sticky bar as well as on the card
    const save = chosen.dataset.save;
    $$('[data-bar-save]').forEach(el => {
      el.hidden = !save;
      if (save) el.textContent = 'Saves $' + save;
    });

    // fallback class for browsers without :has()
    $$('.format').forEach(card => card.classList.toggle('is-selected', card.contains(chosen)));
  }

  function trackFormat() {
    const chosen = chosenFormat();
    if (!chosen) return;
    const price = currentPrice();
    window.orreryTrack('InitiateCheckout', 'begin_checkout', {
      content_type: 'product',
      content_ids: [formatKey(chosen)],
      content_name: currentTitle(),
      num_items: 1,
      currency: CURRENCY,
      value: price
    }, {
      currency: CURRENCY,
      value: price,
      items: [{ item_id: formatKey(chosen), item_name: currentTitle(), price: price, quantity: 1 }]
    });
  }

  $$('input[name="id"]').forEach(r => r.addEventListener('change', () => { syncFormat(); updatePreview(); trackFormat(); }));

  /* ----------------------------------------------------------- FAQ */
  $$('.faq__q').forEach(btn => {
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!open));
      $('#' + btn.getAttribute('aria-controls')).hidden = open;
    });
  });

  /* ----------------------------------------------------------- no draft
     Nothing is restored on load: the page opens on an empty wheel every time,
     so it never shows a chart to someone it does not belong to. A draft key
     from earlier versions is cleared. */
  const saveDraft = () => {};
  try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* storage unavailable */ }

  /* ----------------------------------------------------------- validation */
  // in the order they stand in the hero, so the first one missing is the first
  // one the page scrolls to
  const rules = [
    { id: 'dob',   test: v => v !== '' && new Date(v) <= new Date(),  msg: 'Please enter a date of birth.' },
    { id: 'place', test: v => v.trim().length >= 2,                   msg: 'Please enter the town or city of birth.' },
    { id: 'name',  test: v => v.trim().length >= 2, msg: 'Please enter the name that should appear on the chart.' }
  ];

  function setError(input, msg) {
    const field = input.closest('.orrery-field');
    const out = $('.error', field);
    field.classList.toggle('is-invalid', Boolean(msg));
    input.setAttribute('aria-invalid', msg ? 'true' : 'false');
    if (out) out.textContent = msg || '';
  }

  function validate() {
    let firstBad = null;
    rules.forEach(rule => {
      const input = form.elements[rule.id];
      const ok = rule.test(input.value);
      setError(input, ok ? '' : rule.msg);
      if (!ok && !firstBad) firstBad = input;
    });
    return firstBad;
  }

  // clear an error as soon as the user fixes the field
  rules.forEach(rule => {
    const input = form.elements[rule.id];
    input.addEventListener('input', () => {
      if (input.closest('.orrery-field').classList.contains('is-invalid') && rule.test(input.value)) setError(input, '');
    });
  });

  // date of birth cannot be in the future
  const dob = form.elements.dob;
  if (dob) dob.max = new Date().toISOString().slice(0, 10);

  // Enter in a birth-detail field moves to the next one (which is also what
  // commits a date or time) rather than placing the order
  const detailInputs = $$('[data-birth-fields] input:not([type="hidden"]):not([type="radio"])', form);
  form.addEventListener('keydown', ev => {
    if (ev.key !== 'Enter' || ev.defaultPrevented) return;
    const i = detailInputs.indexOf(ev.target);
    if (i < 0) return;
    ev.preventDefault();
    const next = detailInputs[i + 1];
    if (next) next.focus(); else ev.target.blur();
  });

  /* ----------------------------------------------------------- submit */
  function submitOrder() {
    // The form is Shopify's product form: the chosen variant as `id`, the birth
    // details as properties[...]. It is posted to /cart/add.js so a refusal
    // (sold out, bad variant) can be shown inline instead of on the cart page.
    const body = new FormData(form);
    ['email', 'place_label', 'return_to'].forEach(k => body.delete(k));
    Array.from(body.keys()).forEach(k => { if (/^properties\[/.test(k) && !String(body.get(k)).trim()) body.delete(k); });
    body.set('quantity', '1');
    return fetch((CONFIG.cartAddUrl || '/cart/add') + '.js', {
      method: 'POST', body: body, credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(res => res.json().catch(() => ({})).then(data => {
      if (!res.ok) throw new Error(data.description || data.message || ('That did not go through (' + res.status + '). Please try again.'));
      return data;
    }));
  }

  form.addEventListener('submit', e => {
    e.preventDefault();
    const bad = validate();
    if (bad) {
      scrollToEl(bad.closest('.orrery-field'), 120);
      setTimeout(() => bad.focus({ preventScroll: true }), 400);
      return;
    }
    // nothing to print until a chart has been drawn
    if (!current.chart) { goDraw(); return; }

    const chosen = chosenFormat();
    const order = {
      name: form.elements.name.value.trim(),
      dob: form.elements.dob.value,
      tob: form.elements.tob.value,
      place: form.elements.place.value.trim(),
      location: placeLoc(),   // { label, lat, lon, tz } once the place is matched, else null
      email: form.elements.email.value.trim(),
      recipient: form.elements['properties[Gift]'].value === 'Yes' ? 'gift' : 'me',
      format: formatKey(chosen),
      variantId: chosen.value,
      price: currentPrice()
    };

    window.orreryTrack('AddToCart', 'add_to_cart', {
      content_type: 'product',
      content_ids: [order.format],
      content_name: currentTitle(),
      num_items: 1,
      currency: CURRENCY,
      value: order.price
    }, {
      currency: CURRENCY,
      value: order.price,
      items: [{ item_id: order.format, item_name: currentTitle(), price: order.price, quantity: 1 }]
    });

    // the button pressed (the one under the format cards, or the final one)
    // says "One moment"; both are held while the cart call runs
    const btns = $$('button[type="submit"]', form).concat($$('button[type="submit"][form="order"]'));
    const pressed = (e.submitter && btns.indexOf(e.submitter) >= 0) ? e.submitter : btns[0];
    const errors = [$('#format-error'), $('#order-error')].filter(Boolean);
    const labels = btns.map(b => b.textContent);
    btns.forEach(b => { b.disabled = true; });
    if (pressed) pressed.textContent = 'One moment';
    errors.forEach(el => { el.hidden = true; });

    submitOrder(order).then(() => {
      // Shopify owns everything from here: the confirmation is its order status page
      try { localStorage.removeItem(DRAFT_KEY); } catch (_) {}
      window.location.assign(CONFIG.checkoutUrl || '/checkout');
    }).catch(err => {
      const msg = (err && err.message) || 'That did not go through. Please try again.';
      const near = pressed && pressed.closest('#format') ? $('#format-error') : $('#order-error');
      if (near) { near.textContent = msg; near.hidden = false; }
      btns.forEach((b, i) => { b.disabled = false; b.textContent = labels[i]; });
    });
  });

  /* ----------------------------------------------------------- order bar
     Nothing to buy until the chart is the visitor's own: the bar stays down
     while the example is up. Then it shows once the hero button has scrolled
     away, and hides while the format step or the final call to action is on
     screen. The theme's own sticky header stands in for the design's floating
     header; on narrow screens the stylesheet hides it while the bar is up, so
     the two are never on screen together. */
  const orderbar = $('#orderbar');
  const hero = $('.hero');
  const consentBar = $('#consent');
  const formatStep = $('#format');
  let ownChart = false;         // set by syncChartState once the chart is not the example's
  const onScreen = el => { const r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < window.innerHeight; };
  function onScroll() {
    if (!orderbar || !hero) return;
    const past = hero.getBoundingClientRect().bottom < 0;   // the hero has left the view
    const ctaOnScreen = cta.getBoundingClientRect().top < window.innerHeight;
    const formatOnScreen = formatStep && !formatStep.hidden && onScreen(formatStep);
    const consentOpen = consentBar && !consentBar.hidden;
    const show = ownChart && past && !ctaOnScreen && !formatOnScreen && !consentOpen;
    orderbar.classList.toggle('is-visible', show);
    document.body.classList.toggle('has-orderbar', show);
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  if (pageWrapper) pageWrapper.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  onScroll();

  /* ----------------------------------------------------------- consent bar */
  if (consentBar) {
    if (window.orreryConsent && window.orreryConsent.state() === 'pending') {
      consentBar.hidden = false;
      document.body.classList.add('has-consent');
    }
    $$('[data-consent]', consentBar).forEach(btn => {
      btn.addEventListener('click', () => {
        window.orreryConsent[btn.dataset.consent === 'grant' ? 'grant' : 'deny']();
        consentBar.hidden = true;
        document.body.classList.remove('has-consent');
        onScroll();
      });
    });
  }

  /* ----------------------------------------------------------- live chart preview
     Positions, houses and retrograde flags come from assets/orrery-engine.js
     (loaded before this file), which tests/chart-engine.test.mjs checks against
     Swiss Ephemeris. The birthplace is looked up as it is typed, which gives a
     time zone (so the clock time becomes a real instant) and coordinates (so
     the ascendant and the Placidus cusps follow). With no birth time the chart
     is drawn for sunrise at the birthplace; with no place it falls back to this
     browser's time zone, equal houses and fifteen degrees an hour from sunrise. */
  const ENGINE = window.OrreryEngine;
  const chartFor = ENGINE.chartFor;
  const houseOf = ENGINE.houseOf;
  const tzOffsetMinutes = ENGINE._internal.tzOffsetMinutes;
  const NS = 'http://www.w3.org/2000/svg';
  const wheel = $('#wheel');
  const previewName = $('#preview-name');
  const previewMeta = $('#preview-meta');
  const previewPhase = $('#preview-phase');
  const previewQuote = $('#preview-quote');
  // radii in the 400-unit wheel: hub, houses, planets, the zodiac band with its glyphs, the ring of sign names, the edge
  const CX = 200, CY = 200, R_EDGE = 196, R_OUT = 178, R_BAND = 150, R_INNER = 104, R_PLANET = 128, R_HUB = 44;
  // Colours are written as presentation attributes in the print's navy-and-gold,
  // which is what the email PNG and the printed thumbnail show; the screen
  // chart is restyled by the stylesheet on the page's cream ground, where gold
  // does not read (2.1:1). Sizes below are set for the screen in the stylesheet too.
  const GOLD = '#c9a544';
  // a narrow screen needs bigger glyphs, so they are spread further apart
  const narrow = window.matchMedia('(max-width: 560px)');
  const SIGN_GLYPHS = ['\u2648','\u2649','\u264A','\u264B','\u264C','\u264D','\u264E','\u264F','\u2650','\u2651','\u2652','\u2653'];
  const SIGN_NAMES = ENGINE.SIGN_NAMES;
  const RAD = Math.PI / 180;
  const norm = d => ((d % 360) + 360) % 360;

  const PLANET_INFO = {
    Sun: 'What you are made of, and what you keep returning to.',
    Moon: 'What you need, including what you rarely ask for.',
    Mercury: 'How you think, speak and take things in.',
    Venus: 'What you love, and how you draw people close.',
    Mars: 'How you act, and what you will fight for.',
    Jupiter: 'Where you grow, and where luck finds you.',
    Saturn: 'Where life asks for patience, and where it repays it.',
    Uranus: 'Where you break the pattern, and what you refuse to inherit.',
    Neptune: 'What you long for, and where the edges blur.',
    Pluto: 'What changes you completely, once it has hold.',
    'North Node': 'Where the Moon\'s path crosses the Sun\'s: where you are headed, and what you are still learning.',
    'South Node': 'The opposite crossing: what you came in knowing, and lean on too readily.'
  };
  // the two hues of the aspect figure; weight varies with them so it reads in monochrome too
  const ASPECT_KIND = { 0: 'conj', 60: 'soft', 90: 'hard', 120: 'soft', 180: 'hard' };
  const SIGN_INFO = [['Fire', 'Cardinal'], ['Earth', 'Fixed'], ['Air', 'Mutable'], ['Water', 'Cardinal'],
    ['Fire', 'Fixed'], ['Earth', 'Mutable'], ['Air', 'Cardinal'], ['Water', 'Fixed'],
    ['Fire', 'Mutable'], ['Earth', 'Cardinal'], ['Air', 'Fixed'], ['Water', 'Mutable']];
  const HOUSE_INFO = ['Yourself, and first impressions', 'Money, worth and what you keep', 'Mind, words and siblings',
    'Home, roots and family', 'Play, romance and what you make', 'Work, habits and health',
    'Partnership, and the other person', 'What is shared, and what is let go', 'Belief, study and far travel',
    'Work and your public name', 'Friends, allies and belonging', 'What you keep to yourself'];
  const ASPECT_NAMES = { 60: 'sextile', 90: 'square', 120: 'trine', 180: 'opposite' };

  function node(name, attrs, text) {
    const el = document.createElementNS(NS, name);
    Object.keys(attrs).forEach(k => el.setAttribute(k, attrs[k]));
    if (text != null) el.textContent = text;
    return el;
  }
  function polar(rel, r) {
    const phi = (180 + rel) * RAD;
    return [CX + r * Math.cos(phi), CY - r * Math.sin(phi)];
  }
  function line(rel, r1, r2, attrs) {
    const a = polar(rel, r1), b = polar(rel, r2);
    return node('line', Object.assign({ x1: a[0].toFixed(1), y1: a[1].toFixed(1), x2: b[0].toFixed(1), y2: b[1].toFixed(1), stroke: GOLD }, attrs));
  }
  // annular sector from angle a to b (screen-relative, counter-clockwise) between radii r1 < r2
  function sector(a, b, r1, r2) {
    const p1 = polar(a, r2), p2 = polar(b, r2), p3 = polar(b, r1), p4 = polar(a, r1);
    const f = v => v.toFixed(1);
    return `M${f(p1[0])} ${f(p1[1])} A${r2} ${r2} 0 0 0 ${f(p2[0])} ${f(p2[1])} L${f(p3[0])} ${f(p3[1])} A${r1} ${r1} 0 0 1 ${f(p4[0])} ${f(p4[1])} Z`;
  }
  const fmtDeg = ENGINE.formatDegrees;
  const degLabel = lon => { const f = fmtDeg(lon); return f.text + ' ' + f.sign; };   // "10° 26′ Sagittarius"
  const ordinal = n => n + (n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th');
  const houseLabel = (cusps, lon) => ordinal(houseOf(cusps, lon)) + ' house';
  const phaseLabel = ph => ph.name + ' \u00b7 ' + (ph.illuminated * 100).toFixed(1) + '% illuminated';
  const cap = t => t.charAt(0).toUpperCase() + t.slice(1);

  /* ---- the written readings, assembled from the library in
     assets/orrery-readings.js (built from content/readings.json). Nothing is
     generated at page load and nothing here carries copy of its own. The
     chart selects the entries (sign and house of the Sun and Moon, the rising
     sign, the two elements, the Moon's phase, the chart ruler and its house,
     the kind of the tightest aspect) and the merge fields, computed once in
     assets/orrery-facts.js, put the chart's own values into the prose. An
     entry that is not yet written renders as a marked placeholder naming its key. */
  const READINGS = window.OrreryReadings || {};
  const COMPOSE = window.OrreryCompose;
  const plain = text => ({ text: text, ph: false });
  const pageReading = chart => COMPOSE.compose(chart, 'page', READINGS);
  const fullReading = chart => COMPOSE.compose(chart, 'full', READINGS);
  const rowReading = (chart, b) => COMPOSE.rowReading(chart, b, READINGS);
  // paragraphs into a container, placeholders marked; "\n\n" inside an entry breaks the paragraph
  function renderParas(container, paras) {
    while (container.firstChild) container.removeChild(container.firstChild);
    paras.forEach(segs => {
      let p = document.createElement('p');
      container.appendChild(p);
      segs.forEach(seg => {
        seg.text.split(/\n\s*\n/).forEach((chunk, i) => {
          if (i) { p = document.createElement('p'); container.appendChild(p); }
          else if (p.childNodes.length) p.append(' ');
          if (seg.ph) { const m = document.createElement('mark'); m.className = 'reading__ph'; m.textContent = chunk; p.appendChild(m); }
          else p.append(chunk);
        });
      });
    });
  }
  // the same paragraphs as plain text for the mailer, and for the writer's preview aid (OrreryReading.page() / .full())
  const parasForMail = COMPOSE.forMail;
  const asText = COMPOSE.asText;

  /* ---- there is no example chart. The page opens on an empty wheel and draws
     the visitor's own as the details arrive: the planets by sign from the
     date; the Moon, houses and angles once the time and place are known
     (assets/orrery-analysis.js decides what is honestly computable). Nothing
     below the chart describes anyone but the visitor. */
  const ANALYSIS = window.OrreryAnalysis;
  const current = { stage: 0, chart: null, missing: [], analysis: null };
  // a PNG of the wheel as drawn, for the email; null if the browser cannot render it
  function wheelPng() {
    return new Promise(resolve => {
      try {
        const clone = wheel.cloneNode(true);
        clone.setAttribute('xmlns', NS);
        clone.setAttribute('width', '1200'); clone.setAttribute('height', '1200');
        $$('.pw-hit, .pw-halo', clone).forEach(e => e.remove());
        $$('.pw-aspect', clone).forEach(e => e.setAttribute('opacity', '0.35'));
        $$('.pw', clone).forEach(e => e.removeAttribute('style'));
        clone.insertBefore(node('rect', { x: 0, y: 0, width: 400, height: 400, fill: '#111a2d' }), clone.firstChild);
        const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' }));
        const img = new Image();
        img.onload = () => {
          try {
            const c = document.createElement('canvas'); c.width = c.height = 1200;
            c.getContext('2d').drawImage(img, 0, 0, 1200, 1200);
            resolve(c.toDataURL('image/png'));
          } catch (_) { resolve(null); }
          URL.revokeObjectURL(url);
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      } catch (_) { resolve(null); }
    });
  }
  const b64url = {
    enc: str => btoa(unescape(encodeURIComponent(str))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
    dec: str => decodeURIComponent(escape(atob(str.replace(/-/g, '+').replace(/_/g, '/')))),
    bytes: str => Uint8Array.from(atob(str.replace(/-/g, '+').replace(/_/g, '/')), ch => ch.charCodeAt(0))
  };
  // what the reading email is built from: the details, a summary of the chart, and the wheel
  function chartDetails(email) {
    const loc = placeLoc();
    const chart = current.stage === 3 ? current.chart : null;
    const summary = chart ? {
      sun: SIGN_NAMES[Math.floor(chart.bodies[0].lon / 30)],
      moon: SIGN_NAMES[Math.floor(chart.bodies[1].lon / 30)],
      rising: SIGN_NAMES[Math.floor(chart.asc / 30)],
      placements: chart.bodies.map(b => ({ glyph: b.glyph, name: b.name + (b.node ? ' (true)' : ''), pos: degLabel(b.lon) + (b.retro && !b.node ? ' \u211e' : '') + ' \u00b7 ' + houseLabel(chart.cusps, b.lon) })),
      phase: phaseLabel(chart.lunarPhase)
    } : null;
    return {
      name: form.elements.name.value.trim(), quote: quoteText(), dob: committed.dob, tob: committed.tob,
      place: loc ? loc.label : form.elements.place.value.trim(), location: loc, email: email, summary: summary,
      reading: chart ? parasForMail(fullReading(chart)) : null    // tier three: the full reading, composed here
    };
  }
  function sendChart(details) {
    // the chart delivery Worker (worker/): it computes, stores, draws and emails the chart, and
    // answers with the permanent page; the older mailer path below stays until the theme setting is switched
    if (SERVICES.chartApi) {
      const body = { name: details.name, quote: details.quote || null, sections: details.sections || [], dob: details.dob, tob: details.tob || null, place: details.place, location: details.location, email: details.email };
      return fetch(SERVICES.chartApi.replace(/\/$/, '') + '/api/chart', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        .then(res => res.json().catch(() => ({})).then(data => { if (!res.ok || !data.id) throw new Error(data.error || ('HTTP ' + res.status)); return Object.assign({ ok: true }, data); }));
    }
    return wheelPng().then(png => {
      details.wheelPng = png;
      if (!SERVICES.sendChartUrl) {
        // demo: no mailer yet. The link that would have been emailed:
        const payload = { n: details.name, d: details.dob, t: details.tob, p: details.place,
          la: details.location ? details.location.lat : null, lo: details.location ? details.location.lon : null,
          tz: details.location ? details.location.tz : null, e: details.email, at: Date.now() };
        const here = location.href.split(/[?#]/)[0];
        console.info('Orrery demo: no mailer configured. The email would carry this link:\n' + here + '?c=' + b64url.enc(JSON.stringify(payload)) + '&s=demo');
        return new Promise(r => setTimeout(() => r({ ok: true }), 700));
      }
      return fetch(SERVICES.sendChartUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(details) })
        .then(res => res.json().catch(() => ({})).then(data => { if (!res.ok || !data.ok) throw new Error(data.error || ('HTTP ' + res.status)); return data; }));
    });
  }
  // the details a chart is drawn from; the link from the email opens exactly these
  const chartSig = (dob, tob) => [dob, tob, form.elements.place_lat.value, form.elements.place_lon.value].join('|');

  /* ---- the assistant, under the reading
     Two things have to be true before it appears: the address has been given
     (the reading is unlocked) and the Worker has stored the chart and handed
     back its id. The second is what the Worker checks — a chart row exists only
     because an email was given for it, so it refuses any id without one. The
     page hiding the box is a courtesy; the gate is in worker/src/chat.js. */
  const CHART_ID_KEY = 'orrery.chartIds';
  function chartIds() { try { return JSON.parse(localStorage.getItem(CHART_ID_KEY) || '{}'); } catch (_) { return {}; } }
  function rememberChartId(sig, id) {
    try { const m = chartIds(); m[sig] = id; localStorage.setItem(CHART_ID_KEY, JSON.stringify(m)); } catch (_) {}
    syncChat();
  }
  const chartIdFor = sig => chartIds()[sig] || null;

  const natalBox = $('#natal-chat');
  /* The first of the assistant's two gates: a section setting, off until the
     Worker has been deployed with a key. Off, the page never calls the endpoint
     at all — a cross-origin 404 from a Worker without the route carries no CORS
     headers, and the browser logs it as an error. The second gate is the
     readiness check inside assets/orrery-chat.js. */
  const CHAT_ON = !!(CONFIG.assistantOn);
  let natalChat = null, natalId = null;

  /* Three questions from this chart's own figures: the chart ruler, the
     tightest aspect, and Venus. Written as the visitor would ask them. */
  function natalQuestions(chart) {
    const out = [];
    const facts = window.OrreryFacts && OrreryFacts.factsFor ? OrreryFacts.factsFor(chart, window.OrreryReadings || {}) : null;
    if (facts && facts.ruler) out.push('What does it mean that ' + facts.ruler.name + ' rules my chart?');
    const asp = facts && facts.asp;
    if (asp) out.push('What does my ' + asp.a.name + ' ' + asp.type + ' ' + asp.b.name + ' actually do?');
    const venus = (chart.bodies || []).filter(b => b.key === 'venus')[0];
    if (venus) out.push('What does my Venus in ' + fmtDeg(venus.lon).sign + ' say about how I love?');
    while (out.length < 3) out.push('What is the most unusual thing about this chart?');
    return out.slice(0, 3);
  }

  function syncChat() {
    /* Storefront only. The permanent chart page carries the same markup and the
       same ids, but it mounts the assistant itself from the chart id in its own
       URL — it has no email offer, no outbox and no form to read a signature
       from, and reaching for one here threw on every load of that page. */
    if (!natalBox || !document.getElementById('reading-offer')) return;
    const sig = chartSig(committed.dob, committed.tob);
    const id = unlocked && current.stage === 3 ? chartIdFor(sig) : null;
    if (!id || !CHAT_ON || !window.OrreryChat || !SERVICES.chartApi) { if (natalChat) natalChat.close(); else natalBox.hidden = true; return; }
    if (!natalChat) {
      natalChat = OrreryChat.mount({
        root: natalBox, log: $('#natal-chat-log'), starters: $('#natal-chat-starters'),
        box: $('#natal-chat-form'), input: $('#natal-chat-input'),
        send: $('#natal-chat-send'), note: $('#natal-chat-note'),
        api: SERVICES.chartApi, mode: 'natal',
        reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        payload: () => ({ id: natalId }),
        questions: () => (current.chart ? natalQuestions(current.chart) : [])
      });
    }
    natalId = id;
    natalChat.open(id);
  }

  /* ---- the buy path. The format cards stand under the print, always: there is
     one button to buy and nothing to open first. The hero's button and the
     sticky bar's both lead to the first detail still missing, or, once the
     chart is the visitor's own, down to the cards. */
  const readingOffer = $('#reading-offer');
  function goDraw() {
    // the first thing still missing: the name, the date, the time, or the place
    const need = !form.elements.name.value.trim() ? 'name' : !committed.dob ? 'dob' : !committed.tob ? 'tob' : !placeLoc() ? 'place' : 'name';
    const first = form.elements[need];
    scrollToEl(first.closest('.orrery-field'), 120);
    setTimeout(() => first.focus({ preventScroll: true }), 400);
  }
  function openFormats() {
    if (!formatStep) return;
    scrollToEl(formatStep, 90);
    const chosen = chosenFormat();
    if (chosen) setTimeout(() => chosen.focus({ preventScroll: true }), 450);
  }
  $$('[data-print-cta]').forEach(btn => btn.addEventListener('click', () => { if (!current.chart) goDraw(); else openFormats(); }));
  const detailsAfter = $('#details-after');
  function syncChartState() {
    ownChart = current.stage >= 1;
    // everything below the chart waits for the visitor's own chart
    if (detailsAfter) detailsAfter.hidden = !ownChart;
    // the identity block describes a chart; without one it does not exist
    const plate = $('.preview__plate'); if (plate) plate.hidden = !ownChart;
    const legend = $('.preview__legend'); if (legend) legend.hidden = current.stage < 2;   // no aspect lines until the time is known
    if (readingOffer) readingOffer.hidden = current.stage < 3 || unlocked;   // the whole chart first, and only while it is still asked for
    syncAgain();
    syncJump();
    syncSendButton();
    onScroll();
  }

  /* ---- the shortcut from the plate to the reading. On a phone the reading
     sits a long way below the chart; this is the way down for readers who
     want depth. Shown once the chart is the visitor's own, and only while the
     reading is out of view. Landing puts focus on the reading's heading. */
  const readingJump = $('#reading-jump'), readingSection = $('#chapter-reading');
  let readingInView = false;
  function syncJump() { if (readingJump) readingJump.hidden = current.stage < 3 || readingInView; }
  if (readingJump && readingSection) {
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => { readingInView = entries.some(e => e.isIntersecting); syncJump(); }, { threshold: 0.05 }).observe(readingSection);
    }
    // the same landing as the hero's button: intoReading() is the only route in
    readingJump.addEventListener('click', ev => { ev.preventDefault(); intoReading(); });
  }

  /* ---- tier three, the full reading by email: the one thing the address unlocks */
  let sentFor = '';
  const sendBtn = $('[data-send-reading]');
  const sentNote = $('#reading-sent');
  const emailEl = form.elements.email;
  const okEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

  /* ---- the outbox
     An address handed over has to reach somebody. The reading opens at once and
     never waits on the network, so the send is queued instead: three tries this
     visit with a widening gap, the queue kept on the device, and a fresh run of
     tries on the next load if it still has not gone. Only when a whole run has
     failed does one quiet line say so and offer the try again — a failure is
     worth one sentence, not a banner. */
  const QUEUE_KEY = 'orrery.reading.queue', SENT_KEY = 'orrery.reading.sent';
  const RETRY_WAITS = [4000, 15000, 45000];
  const retryLine = $('#reading-retry'), retryBtn = $('#reading-retry-btn'), retryText = $('#reading-retry-line');
  const againLine = $('#reading-again'), againBtn = $('#reading-again-btn'), againTo = $('#reading-again-to'), againText = $('#reading-again-line');
  const readQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || 'null'); } catch (_) { return null; } };
  const writeQueue = q => { try { q ? localStorage.setItem(QUEUE_KEY, JSON.stringify(q)) : localStorage.removeItem(QUEUE_KEY); } catch (_) {} };
  const storedEmail = () => { try { const v = localStorage.getItem(UNLOCK_KEY); return v && v.indexOf('@') > 0 ? v : ''; } catch (_) { return ''; } };
  try { sentFor = localStorage.getItem(SENT_KEY) || ''; } catch (_) {}
  let retrying = false;

  function markSent(sig, email) {
    if (sig) { sentFor = sig; try { localStorage.setItem(SENT_KEY, sig); } catch (_) {} }
    writeQueue(null);
    if (retryLine) retryLine.hidden = true;
    if (againLine && email) { againText.textContent = 'Sent to ' + email + '.'; againBtn.hidden = true; againLine.hidden = false; }
    syncSendButton();
  }

  // what the Worker needs to draw and send this chart, small enough to keep on the device
  function outboxItem(email, picks) {
    const loc = placeLoc();
    return {
      email: email, name: form.elements.name.value.trim(), quote: quoteText(), dob: committed.dob, tob: committed.tob,
      place: loc ? loc.label : form.elements.place.value.trim(), location: loc,
      // which sections they asked for; empty means all of it, which is also what
      // an untouched set means — ticking nothing is not asking for nothing
      sections: picks && picks.length ? picks.slice() : [],
      sig: chartSig(committed.dob, committed.tob), at: Date.now()
    };
  }

  function flushQueue() {
    if (retrying || !readQueue()) return;
    retrying = true;
    if (retryLine) retryLine.hidden = true;
    let n = 0;
    const attempt = () => {
      const item = readQueue();
      if (!item) { retrying = false; return; }
      sendChart(item).then(res => {
        retrying = false;
        markSent(item.sig, item.email);
        // the permanent chart's id: the assistant answers only for a chart the
        // Worker has actually stored, so this is what opens it
        if (res && res.id) rememberChartId(item.sig, res.id);
        window.orreryTrack && window.orreryTrack('Lead', 'generate_lead', { content_name: 'written reading by email' }, { method: 'reading email' });
      }).catch(() => {
        n += 1;
        item.tries = (item.tries || 0) + 1;
        writeQueue(item);
        const wait = RETRY_WAITS[n - 1];
        if (wait != null) { setTimeout(attempt, wait); return; }
        // a whole run has failed: say it once, quietly, and leave the queue for the next load
        retrying = false;
        if (retryText) retryText.textContent = 'That has not reached ' + item.email + ' yet. Your reading stays where it is.';
        if (retryLine) retryLine.hidden = false;
        if (againLine) againLine.hidden = true;
      });
    };
    attempt();
  }

  function queueAndSend(email, picks) {
    if (!okEmail(email)) return;
    writeQueue(outboxItem(email, picks));
    if (againLine) againLine.hidden = true;
    flushQueue();
  }

  /* The line a device that has already given an address sees on its next chart:
     nothing is sent behind them, and nothing is passed over in silence. */
  function syncAgain() {
    if (!againLine) return;
    // "Sent to …" stands until the chart changes under it
    if (againBtn.hidden) { if (sentFor === chartSig(committed.dob, committed.tob)) return; againBtn.hidden = false; }
    const email = storedEmail();
    const waiting = unlocked && current.stage === 3 && email && sentFor !== chartSig(committed.dob, committed.tob);
    if (!waiting || readQueue() || (retryLine && !retryLine.hidden)) { againLine.hidden = true; return; }
    againText.textContent = '';
    againText.append('Send this one to ');
    againTo.textContent = email;
    againText.appendChild(againTo);
    againText.append(' as well?');
    againLine.hidden = false;
  }

  /* The address opens the reading and sends it. The opening does not wait on the
     sending: the text is theirs the moment they hand the address over, and the
     email follows from the outbox above. */
  function submitReading() {
    const email = emailEl.value.trim();
    if (!okEmail(email)) { setError(emailEl, 'Enter your email and we will send your full reading.'); emailEl.focus({ preventScroll: true }); return; }
    setError(emailEl, '');
    unlockReading(email);
    queueAndSend(email, []);
  }
  if (sendBtn) {
    sendBtn.addEventListener('click', submitReading);
    // Enter in the email field sends the reading; it must not place the order
    emailEl.addEventListener('keydown', ev => { if (ev.key === 'Enter') { ev.preventDefault(); submitReading(); } });
  }
  if (retryBtn) retryBtn.addEventListener('click', () => { if (retryLine) retryLine.hidden = true; flushQueue(); });
  if (againBtn) againBtn.addEventListener('click', () => queueAndSend(storedEmail()));
  // anything left in the outbox from a previous visit gets another run of tries
  flushQueue();

  // a fresh set of details can be sent again
  function syncSendButton() {
    if (!sendBtn) return;
    const same = sentFor && sentFor === chartSig(committed.dob, committed.tob);
    sendBtn.disabled = !!same;
    sendBtn.textContent = same ? 'Sent' : 'Read the rest';
    if (sentNote && !same) sentNote.hidden = true;
  }

  /* ---- the link from the email: signed details that open that chart here */
  async function openFromLink() {
    const params = new URLSearchParams(location.search);
    const c = params.get('c'), sg = params.get('s');
    if (!c || !sg) return;
    let payload;
    try { payload = JSON.parse(b64url.dec(c)); } catch (_) { return; }
    let trusted = false;
    if (SERVICES.linkPublicKey && window.crypto && crypto.subtle) {
      try {
        const key = await crypto.subtle.importKey('jwk', SERVICES.linkPublicKey, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
        trusted = await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, b64url.bytes(sg), new TextEncoder().encode(c));
      } catch (_) { trusted = false; }
    } else if (SERVICES.acceptUnsignedLinks) {
      trusted = true;
    }
    if (!trusted || !payload || !payload.d) return;
    form.elements.name.value = payload.n || '';
    form.elements.dob.value = payload.d || '';
    form.elements.tob.value = payload.t || '';
    form.elements.place.value = payload.p || '';
    form.elements.place_label.value = payload.tz ? (payload.p || '') : '';
    form.elements.place_lat.value = payload.tz && payload.la != null ? Number(payload.la).toFixed(4) : '';
    form.elements.place_lon.value = payload.tz && payload.lo != null ? Number(payload.lo).toFixed(4) : '';
    form.elements.place_tz.value = payload.tz || '';
    if (payload.e && !form.elements.email.value) form.elements.email.value = payload.e;
    commitBirth(true);
    saveDraft();
    renderPlaceNote();
    updatePreview();
    try { history.replaceState(null, '', location.pathname + location.hash); } catch (_) {}
    scrollToEl($('#scene'), 90);
  }

  /* ---- the link from a chart page: /c/<id>'s "have it printed" arrives here as
     ?chart=<id>; the details are fetched from the chart API and the form filled,
     so the print is ordered from exactly the chart that was shared. */
  async function openFromChartId() {
    if (GIVEN) return;
    const id = new URLSearchParams(location.search).get('chart');
    if (!id || !/^[A-Za-z0-9]{10}$/.test(id) || !SERVICES.chartApi) return;
    let rec;
    try { const r = await fetch(SERVICES.chartApi.replace(/\/$/, '') + '/api/chart/' + id); if (!r.ok) return; rec = await r.json(); } catch (_) { return; }
    if (!rec || !rec.birth_date) return;
    form.elements.name.value = rec.name || '';
    form.elements.dob.value = rec.birth_date;
    form.elements.tob.value = rec.birth_time || '';
    form.elements.place.value = rec.place_name || '';
    form.elements.place_label.value = rec.place_name || '';
    form.elements.place_lat.value = Number(rec.lat).toFixed(4);
    form.elements.place_lon.value = Number(rec.lon).toFixed(4);
    form.elements.place_tz.value = rec.tz || '';
    const chartRef = form.elements['properties[_chart]']; if (chartRef) chartRef.value = id;   // the order then names the chart the proof shows
    commitBirth(true);
    saveDraft();
    renderPlaceNote();
    updatePreview();
    try { history.replaceState(null, '', location.pathname + location.hash); } catch (_) {}
    if (current.chart) openFormats(); else scrollToEl($('#scene'), 90);
  }

  /* ---- tooltip, highlight and pinning */
  const tip = $('#preview-tip');
  const panel = $('.preview__panel');
  const placements = $('#placements');
  let pinned = null;

  function showTip(el) {
    if (!tip || !el || !panel) return;
    $('.preview__tip-glyph', tip).textContent = el.dataset.glyph || '';
    $('.preview__tip-name', tip).textContent = el.dataset.label || '';
    $('.preview__tip-detail', tip).textContent = el.dataset.detail || '';
    $('.preview__tip-more', tip).textContent = el.dataset.more || '';
    tip.hidden = false;
    const pr = panel.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const tw = tip.offsetWidth, th = tip.offsetHeight;
    const cx = er.left + er.width / 2 - pr.left;
    let x = cx - tw / 2;
    x = Math.max(6, Math.min(x, pr.width - tw - 6));
    let y = er.top - pr.top - th - 10;
    const below = y < 4;
    if (below) y = er.bottom - pr.top + 10;
    tip.classList.toggle('is-below', below);
    tip.style.setProperty('--ax', Math.round(Math.max(12, Math.min(cx - x, tw - 12))) + 'px');
    tip.style.transform = 'translate(' + Math.round(x) + 'px, ' + Math.round(y) + 'px)';
  }
  function hideTip() { if (tip) tip.hidden = true; }

  function setLit(el, on) {
    if (!el || !wheel) return;
    el.classList.toggle('is-lit', on);
    wheel.classList.toggle('has-focus', on && el.classList.contains('pw-planet'));
    const id = el.dataset.id;
    if (id) {
      $$('.pw-aspect', wheel).forEach(l => l.classList.toggle('is-lit', on && (l.dataset.a === id || l.dataset.b === id)));
      if (placements) $$('.placements__row', placements).forEach(r => r.classList.toggle('is-lit', on && r.dataset.target === id));
    }
    if (el.dataset.sign != null) {
      const sg = $('.pw-sign[data-index="' + el.dataset.sign + '"]', wheel);
      if (sg) sg.classList.toggle('is-linked', on);
    }
  }
  function hoverIn(el) {
    if (pinned && pinned !== el) setLit(pinned, false);
    setLit(el, true);
    showTip(el);
  }
  function hoverOut(el) {
    if (pinned === el) return;
    setLit(el, false);
    if (pinned) { setLit(pinned, true); showTip(pinned); } else hideTip();
  }
  function pin(el) {
    if (pinned === el) { unpin(); return; }
    if (pinned) setLit(pinned, false);
    pinned = el;
    setLit(el, true);
    showTip(el);
    if (placements) $$('.placements__row', placements).forEach(r => r.classList.toggle('is-pinned', r.dataset.target === el.dataset.id));
  }
  function unpin() {
    if (pinned) setLit(pinned, false);
    pinned = null;
    hideTip();
    if (placements) $$('.placements__row.is-pinned', placements).forEach(r => r.classList.remove('is-pinned'));
  }
  const targetOf = ev => ev.target.closest ? ev.target.closest('.pw') : null;
  if (wheel) {
    wheel.addEventListener('mouseover', ev => { const t = targetOf(ev); if (t && !(ev.relatedTarget && t.contains(ev.relatedTarget))) hoverIn(t); });
    wheel.addEventListener('mouseout', ev => { const t = targetOf(ev); if (t && !(ev.relatedTarget && t.contains(ev.relatedTarget))) hoverOut(t); });
    wheel.addEventListener('focusin', ev => { const t = targetOf(ev); if (t) hoverIn(t); });
    wheel.addEventListener('focusout', ev => { const t = targetOf(ev); if (t) hoverOut(t); });
    wheel.addEventListener('click', ev => { const t = targetOf(ev); if (t) { ev.preventDefault(); pin(t); } });
    wheel.addEventListener('keydown', ev => {
      const t = targetOf(ev);
      if (t && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); pin(t); }
    });
  }
  if (placements) {
    const rowTarget = row => row && row.dataset.target ? $('.pw[data-id="' + row.dataset.target + '"]', wheel) : null;
    placements.addEventListener('mouseover', ev => { const row = ev.target.closest('.placements__row'); const t = rowTarget(row); if (t) hoverIn(t); });
    placements.addEventListener('mouseout', ev => { const row = ev.target.closest('.placements__row'); const t = rowTarget(row); if (t && !(ev.relatedTarget && row.contains(ev.relatedTarget))) hoverOut(t); });
    placements.addEventListener('focusin', ev => { const t = rowTarget(ev.target.closest('.placements__row')); if (t) hoverIn(t); });
    placements.addEventListener('focusout', ev => { const t = rowTarget(ev.target.closest('.placements__row')); if (t) hoverOut(t); });
    // a click (or Enter / Space on the button) opens the row's reading beneath it
    // and lights the planet on the wheel; several rows can be open at once
    placements.addEventListener('click', ev => {
      const btn = ev.target.closest('.placements__row');
      if (!btn) return;
      const open = btn.getAttribute('aria-expanded') !== 'true';
      setExpanded(btn, open);
      const t = rowTarget(btn);
      if (t) { if (open) { if (pinned !== t) pin(t); } else if (pinned === t) unpin(); }
      if (open && !setExpanded.tracked) { setExpanded.tracked = true; window.orreryTrack && window.orreryTrack('ViewContent', 'view_item', { content_name: 'placement reading' }, { content_name: 'placement reading' }); }
    });
  }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function setExpanded(btn, open) {
    const item = btn.closest('.placements__item'), panel = item && $('.placements__panel', item);
    if (!panel) return;
    btn.setAttribute('aria-expanded', String(open));
    const more = $('.placements__more', btn); if (more) more.textContent = open ? 'less' : 'more';
    if (open) {
      panel.hidden = false;
      if (!reduceMotion.matches) void panel.offsetHeight;   // start the transition from closed
      item.classList.add('is-open');
    } else {
      item.classList.remove('is-open');
      if (reduceMotion.matches) { panel.hidden = true; return; }
      const done = () => { panel.removeEventListener('transitionend', done); if (!item.classList.contains('is-open')) panel.hidden = true; };
      panel.addEventListener('transitionend', done);
      setTimeout(done, 450);
    }
  }
  document.addEventListener('click', ev => {
    if (!pinned) return;
    if ((wheel && wheel.contains(ev.target)) || (placements && placements.contains(ev.target))) return;
    unpin();
  });
  document.addEventListener('keydown', ev => { if (ev.key === 'Escape') unpin(); });

  /* ---- drawing */
  // `into` names the svg to draw into: the preview wheel below the fold by
  // default, the hero's framed print when that asks for a chart of its own.
  // One renderer, two canvases; nothing about the drawing itself differs.
  function drawWheel(chart, into) {
    const svgEl = into || wheel;
    if (!svgEl) return;
    unpin();
    svgEl.classList.remove('is-drawn', 'has-focus');
    while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);
    const asc = chart ? chart.asc : 0;
    const angles = !!(chart && chart.anglesKnown);
    // solar houses (no birth time): equal from the Sun, which takes the left horizon; drawn, but never numbered or claimed as angles
    const houses = angles || !!(chart && chart.solarHouses && chart.cusps);
    // cusps relative to the ascendant, which sits on the left horizon; twelve equal divisions
    // as scaffold while there is no horizon yet (no chart, or a chart without a time and place)
    const cusps = houses ? chart.cusps.map(c => norm(c - asc)) : Array.from({ length: 12 }, (_, h) => h * 30);
    let seq = 0;

    // on a narrow screen the sign names are dropped and the zodiac band takes their ring
    const names = !narrow.matches;
    const glyphR = names ? (R_OUT + R_BAND) / 2 : (R_BAND + R_EDGE) / 2;
    [[R_EDGE, 1.3, 0.9]].concat(names ? [[R_OUT, 0.8, 0.8]] : []).concat([[R_BAND, 0.8, 0.8], [R_INNER, 0.8, 0.8], [R_HUB, 0.8, 0.45]]).forEach(([r, w, o], k) => {
      svgEl.appendChild(node('circle', { cx: CX, cy: CY, r: r, fill: 'none', stroke: GOLD, 'stroke-width': w, opacity: o, pathLength: 1, class: 'pw-ring', style: '--i:' + k }));
    });

    // houses: twelve sectors inside the inner ring, numbered once the horizon is known;
    // before that, twelve quiet divisions with nothing to say
    for (let h = 0; h < 12; h++) {
      const from = cusps[h], span = norm(cusps[(h + 1) % 12] - from) || 30;
      if (!houses) { svgEl.appendChild(line(from, R_HUB, R_INNER, { 'stroke-width': 0.5, opacity: 0.25, class: 'pw-scaffold' })); continue; }
      if (!angles) { svgEl.appendChild(line(from, R_HUB, R_INNER, { 'stroke-width': 0.6, opacity: 0.4, class: 'pw-solar' })); continue; }   // solar: the line, no number, no control
      const g = node('g', {
        class: 'pw pw-house', tabindex: 0, role: 'button', 'data-kind': 'house',
        'data-label': 'House ' + (h + 1), 'data-detail': HOUSE_INFO[h],
        'data-more': 'Cusp at ' + degLabel(chart.cusps[h]),
        'aria-label': 'House ' + (h + 1) + ': ' + HOUSE_INFO[h], style: '--i:' + (seq++)
      });
      g.appendChild(node('path', { d: sector(from, from + span, R_HUB, R_INNER), class: 'pw-hit' }));
      g.appendChild(line(from, R_HUB, R_INNER, { 'stroke-width': 0.6, opacity: 0.4 }));
      const q = polar(from + span / 2, R_HUB + 12);
      g.appendChild(node('text', { x: q[0].toFixed(1), y: (q[1] + 4).toFixed(1), fill: GOLD, 'font-size': 11, 'text-anchor': 'middle', opacity: 0.7, 'font-family': 'serif', class: 'pw-house-num' }, String(h + 1)));
      svgEl.appendChild(g);
    }

    // signs: the outer band, each a sector with its ticks and glyph
    SIGN_GLYPHS.forEach((glyph, i) => {
      const a = i * 30 - asc;
      const g = node('g', {
        class: 'pw pw-sign', tabindex: 0, role: 'button', 'data-kind': 'sign', 'data-index': i,
        'data-glyph': glyph, 'data-label': SIGN_NAMES[i], 'data-detail': SIGN_INFO[i][0] + ' \u00b7 ' + SIGN_INFO[i][1],
        'data-more': chart ? 'Begins at ' + degLabel(i * 30) + ' on the wheel.' : '',
        'aria-label': SIGN_NAMES[i] + ', ' + SIGN_INFO[i][0] + ', ' + SIGN_INFO[i][1], style: '--i:' + (seq++)
      });
      g.appendChild(node('path', { d: sector(a, a + 30, R_BAND, R_EDGE), class: 'pw-hit' }));
      // the empty scaffold has the sector boundaries alone: no ticks, no glyphs, no names
      if (!chart) { g.appendChild(line(a, R_BAND, R_EDGE, { 'stroke-width': 0.8, opacity: 0.8 })); svgEl.appendChild(g); return; }
      for (let d = 0; d < 30; d += 5) {
        const major = d === 0;
        g.appendChild(line(a + d, R_BAND, major ? R_EDGE : R_BAND + 6, { 'stroke-width': major ? 0.8 : 0.5, opacity: major ? 0.8 : 0.45 }));
      }
      const q = polar(a + 15, glyphR);
      g.appendChild(node('text', {
        x: q[0].toFixed(1), y: (q[1] + 6).toFixed(1), fill: GOLD, 'font-size': 17,
        'text-anchor': 'middle', opacity: 0.9, 'font-family': 'serif', class: 'pw-glyph',
        style: 'font-variant-emoji: text'
      }, glyph + '\uFE0E'));
      // the sign's name in the outer ring, set along the circle and kept upright
      const mid = norm(a + 15), lower = mid > 180 && mid < 360 ? false : true;   // rel 0..180 is the lower half of the wheel (east to west under the horizon)
      const n = polar(mid, lower ? (R_OUT + R_EDGE) / 2 + 3 : (R_OUT + R_EDGE) / 2 - 3);
      const rot = lower ? 90 - (180 + mid) + 180 : 90 - (180 + mid);
      if (names) g.appendChild(node('text', {
        x: n[0].toFixed(1), y: n[1].toFixed(1), fill: GOLD, 'font-size': 8.5, 'text-anchor': 'middle', opacity: 0.85,
        'font-family': 'serif', 'letter-spacing': '1', class: 'pw-signname', transform: 'rotate(' + rot.toFixed(1) + ' ' + n[0].toFixed(1) + ' ' + n[1].toFixed(1) + ')'
      }, SIGN_NAMES[i].toUpperCase()));
      svgEl.appendChild(g);
    });

    if (!chart) {
      // the invitation, in the hub: an empty chart, not a broken one
      ['Enter your birth details', 'to draw your chart.'].forEach((t, k) => {
        svgEl.appendChild(node('text', { x: CX, y: CY - 4 + k * 13, fill: GOLD, 'font-size': 9.5, 'text-anchor': 'middle', 'font-family': 'serif', 'letter-spacing': '0.4', opacity: 0.9, class: 'pw-invite' }, t));
      });
      svgEl.setAttribute('aria-label', 'An empty chart wheel. Enter your birth details to draw your chart.');
      requestAnimationFrame(() => requestAnimationFrame(() => svgEl.classList.add('is-drawn')));
      return;
    }

    // the angles: the horizon (ascendant-descendant) and the meridian (midheaven-imum coeli)
    // as dashed axes across the wheel, each one control with both ends labelled
    const mcRel = norm(chart.mc - chart.asc);
    const axisNote = '';
    if (angles) [
      { cls: 'pw-asc', rel: 0, labels: ['ASC', 'DSC'], name: 'Ascendant \u2013 Descendant', lon: chart.asc, sign: Math.floor(chart.asc / 30),
        detail: 'ASC ' + degLabel(chart.asc) + ' \u00b7 DSC ' + degLabel(chart.asc + 180),
        more: axisNote + 'The eastern horizon: how you meet people, and how they tend to read you. The descendant opposite is what you look for in others.' },
      { cls: 'pw-mc', rel: mcRel, labels: ['MC', 'IC'], name: 'Midheaven \u2013 Imum Coeli', lon: chart.mc, sign: Math.floor(chart.mc / 30),
        detail: 'MC ' + degLabel(chart.mc) + ' \u00b7 IC ' + degLabel(chart.mc + 180),
        more: axisNote + 'The meridian: the midheaven is your public life and vocation; the imum coeli beneath is home and roots.' }
    ].forEach(ax => {
      const g = node('g', {
        class: 'pw pw-axis ' + ax.cls, tabindex: 0, role: 'button', 'data-kind': 'axis', 'data-sign': ax.sign,
        'data-label': ax.name, 'data-detail': ax.detail, 'data-more': ax.more,
        'aria-label': ax.name + ': ' + ax.detail, style: '--i:' + (seq++)
      });
      const a = polar(ax.rel, R_INNER), b = polar(ax.rel + 180, R_INNER);
      g.appendChild(node('line', { x1: a[0].toFixed(1), y1: a[1].toFixed(1), x2: b[0].toFixed(1), y2: b[1].toFixed(1), stroke: GOLD, 'stroke-width': 1.1, 'stroke-dasharray': '4 3', opacity: 0.9, class: 'pw-axis-line' }));
      [ax.rel, ax.rel + 180].forEach((rel, k) => {
        const hit = polar(rel, R_INNER + 2);
        g.appendChild(node('circle', { cx: hit[0].toFixed(1), cy: hit[1].toFixed(1), r: 13, class: 'pw-hit' }));
        g.appendChild(line(rel, R_INNER - 6, R_BAND, { 'stroke-width': 1.4, opacity: 0.95 }));
        const q = polar(rel, R_INNER - 16);
        g.appendChild(node('text', { x: q[0].toFixed(1), y: (q[1] + 3).toFixed(1), fill: GOLD, 'font-size': 8, 'text-anchor': 'middle', 'font-family': 'serif', class: 'pw-glyph' }, ax.labels[k]));
      });
      svgEl.appendChild(g);
    });

    // planets and nodes. Aspects and ticks use the exact longitude; the glyphs
    // are nudged apart where they crowd, and each tick runs from its glyph
    // back to the true degree on the zodiac ring.
    const MIN_GAP = narrow.matches ? 13 : 10;
    // without a time and place the Moon is not drawn: it moves about thirteen degrees a day
    const placed = chart.bodies.map((b, i) => ({ b: b, i: i, rel: norm(b.lon - chart.asc), shown: 0 })).filter(p => chart.moonKnown || p.b.key !== 'moon').sort((m, n) => m.rel - n.rel);
    // relax in unwrapped angle so neighbours keep their order: each crowded pair is pushed apart evenly
    const pos = placed.map(p => p.rel), count = pos.length;
    for (let pass = 0; pass < 200 && count > 1; pass++) {
      let moved = false;
      for (let i = 0; i < count; i++) {
        const j = (i + 1) % count;
        const gap = (i === count - 1 ? pos[j] + 360 : pos[j]) - pos[i];
        if (gap < MIN_GAP - 0.01) { const d = (MIN_GAP - gap) / 2; pos[i] -= d; pos[j] += d; moved = true; }
      }
      if (!moved) break;
    }
    placed.forEach((p, i) => { p.shown = norm(pos[i]); });

    // the aspect figure: lines between exact longitudes on the inner ring. Hard
    // aspects in red, soft in grey, conjunctions as a heavier grey arc.
    const aspectsOf = {};
    const aspectable = chart.moonKnown ? placed.filter(p => !p.b.node) : [];   // aspects wait for the whole chart
    for (let i = 0; i < aspectable.length; i++) {
      for (let j = i + 1; j < aspectable.length; j++) {
        let sep = Math.abs(aspectable[i].rel - aspectable[j].rel);
        if (sep > 180) sep = 360 - sep;
        const kind = [0, 60, 90, 120, 180].find(t => Math.abs(sep - t) < 5);
        if (kind == null) continue;
        const ia = 'p' + aspectable[i].i, ib = 'p' + aspectable[j].i;
        (aspectsOf[ia] = aspectsOf[ia] || []).push(ASPECT_NAMES[kind] + ' ' + aspectable[j].b.name);
        (aspectsOf[ib] = aspectsOf[ib] || []).push(ASPECT_NAMES[kind] + ' ' + aspectable[i].b.name);
        const cls = 'pw-aspect is-' + ASPECT_KIND[kind];
        if (kind === 0) {
          const lo = Math.min(aspectable[i].rel, aspectable[j].rel), hi = Math.max(aspectable[i].rel, aspectable[j].rel);
          const [from, to] = hi - lo > 180 ? [hi, lo + 360] : [lo, hi];
          const a = polar(from, R_INNER - 3), b = polar(to, R_INNER - 3);
          svgEl.appendChild(node('path', { d: 'M' + a[0].toFixed(1) + ' ' + a[1].toFixed(1) + ' A' + (R_INNER - 3) + ' ' + (R_INNER - 3) + ' 0 0 0 ' + b[0].toFixed(1) + ' ' + b[1].toFixed(1), fill: 'none', stroke: GOLD, 'stroke-width': 2.4, class: cls, 'data-a': ia, 'data-b': ib }));
        } else {
          const a = polar(aspectable[i].rel, R_INNER), b = polar(aspectable[j].rel, R_INNER);
          svgEl.appendChild(node('line', { x1: a[0].toFixed(1), y1: a[1].toFixed(1), x2: b[0].toFixed(1), y2: b[1].toFixed(1), stroke: GOLD, 'stroke-width': kind === 90 || kind === 180 ? 0.9 : 0.6, class: cls, 'data-a': ia, 'data-b': ib }));
        }
      }
    }

    placed.forEach(p => {
      const id = 'p' + p.i;
      const sign = Math.floor(p.b.lon / 30);
      const house = chart.cusps ? houseLabel(chart.cusps, p.b.lon) : 'house not yet placed';
      const asp = aspectsOf[id] || [];
      const retro = p.b.retro && !p.b.node;
      const label = p.b.name + (p.b.node ? ' (true)' : '') + (retro ? ' \u211e' : '');
      const more = (p.b.name === 'Moon' ? phaseLabel(chart.lunarPhase) + '. ' : '') + PLANET_INFO[p.b.name] + (asp.length ? ' ' + cap(asp.join(', ')) + '.' : '');
      const g = node('g', {
        class: 'pw pw-planet' + (retro ? ' is-retro' : '') + (p.b.node ? ' pw-node' : ''), tabindex: 0, role: 'button', 'data-kind': 'planet', 'data-id': id, 'data-sign': sign,
        'data-glyph': p.b.glyph, 'data-label': label, 'data-detail': chart.stage >= 2 ? degLabel(p.b.lon) + (chart.cusps ? '' : '') + ' \u00b7 ' + house + (retro ? ' \u00b7 retrograde' : '') : SIGN_NAMES[sign] + ' \u00b7 by sign until the time and place are known' + (retro ? ' \u00b7 retrograde' : ''),
        'data-more': more,
        'aria-label': p.b.name + (p.b.node ? ' (true node)' : '') + (chart.stage >= 2 ? ' at ' + degLabel(p.b.lon) + (chart.cusps ? ' in the ' + house : '') : ' in ' + SIGN_NAMES[sign]) + (retro ? ', retrograde' : ''), style: '--i:' + (seq++)
      });
      // the tick: from the glyph's edge to the exact degree on the zodiac ring
      const edge = polar(p.shown, R_PLANET + 14), exact = polar(p.rel, R_BAND);
      g.appendChild(node('line', { x1: edge[0].toFixed(1), y1: edge[1].toFixed(1), x2: exact[0].toFixed(1), y2: exact[1].toFixed(1), stroke: GOLD, 'stroke-width': 0.7, opacity: 0.85, class: 'pw-tick' }));
      const q = polar(p.shown, R_PLANET);
      g.appendChild(node('circle', { cx: q[0].toFixed(1), cy: q[1].toFixed(1), r: 15, class: 'pw-hit' }));
      g.appendChild(node('circle', { cx: q[0].toFixed(1), cy: q[1].toFixed(1), r: 13, class: 'pw-halo' }));
      g.appendChild(node('text', {
        x: q[0].toFixed(1), y: (q[1] + (p.b.node ? 6 : 7)).toFixed(1), fill: GOLD, 'font-size': p.b.node ? 16 : 19,
        'text-anchor': 'middle', 'font-family': 'serif', class: 'pw-glyph'
      }, p.b.glyph));
      if (retro) g.appendChild(node('text', { x: (q[0] + 9).toFixed(1), y: (q[1] - 6).toFixed(1), fill: GOLD, 'font-size': 8, 'font-family': 'serif', class: 'pw-retro' }, '\u211e'));
      svgEl.appendChild(g);
    });

    const sunSign = SIGN_NAMES[Math.floor(chart.bodies[0].lon / 30)];
    const moonSign = SIGN_NAMES[Math.floor(chart.bodies[1].lon / 30)];
    const rising = SIGN_NAMES[Math.floor(chart.asc / 30)];
    svgEl.setAttribute('aria-label', chart.solarHouses
      ? 'Chart preview. Sun in ' + sunSign + ', Moon in ' + moonSign + '. No birth time: drawn for sunrise with solar houses, no rising sign.'
      : chart.stage === 3
      ? 'Chart preview. Sun in ' + sunSign + ', Moon in ' + moonSign + ' (' + phaseLabel(chart.lunarPhase).toLowerCase() + '), rising sign ' + rising + '.'
      : 'Chart preview, planets by sign. Sun in ' + sunSign + '. Add a birth time and place for the Moon, houses and angles.');
    requestAnimationFrame(() => requestAnimationFrame(() => svgEl.classList.add('is-drawn')));
  }

  // a print carries no interaction: no hit areas, no haloes, nothing focusable
  // the halo stays: on the hero it is the ring that lights under the finger, and
  // it is invisible until then
  function stripForPrint(el) {
    $$('.pw-hit, .pw-retro', el).forEach(e => e.remove());
    $$('[tabindex], [role]', el).forEach(e => { e.removeAttribute('tabindex'); e.removeAttribute('role'); e.removeAttribute('aria-label'); });
  }

  /* ------------------------------------------------- the hero's framed print
     The poster in the hero is not a picture of a chart, it is the chart: the
     same drawWheel above, drawn into its own svg in the print's navy and gold.
     Before a detail is typed it carries the sky as it stands right now, so the
     frame is never empty; from the first committed detail it is the visitor's
     own. Everything below the fold keeps the older rule and waits. */
  const heroWheel = $('#hero-wheel');
  const heroPlateName = $('#hero-plate-name');
  const heroPlateMeta = $('#hero-plate-meta');
  const heroPlateQuote = $('#hero-plate-quote');
  const heroCaption = $('#hero-caption');
  const heroAwait = $('#hero-await');
  const HERO_CAPTION = heroCaption ? heroCaption.textContent : '';

  // The date and time a plate carries, read back from the instant the chart was
  // actually computed for, so the label can never disagree with the wheel.
  function plateBits(chart, place) {
    const bits = [];
    if (chart) {
      const used = new Date(Math.round((chart.jdUT - 2440587.5) * 86400000 + chart.offset * 60000));
      bits.push(used.toLocaleDateString('en-US', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' }));
      if (chart.solarHouses) bits.push('birth time unknown: drawn for sunrise, solar houses');
      else if (chart.stage >= 2) bits.push(used.toLocaleTimeString('en-US', { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' }));
    }
    if (place) bits.push(place);
    if (chart && chart.zoneAssumed) bits.push('time zone taken from this device until a birthplace is added');
    return bits;
  }

  // The sky now, whole. A horizon needs a place, and before anyone has said
  // where they are the device's own meridian stands in: longitude from its UTC
  // offset, a mid-northern latitude. The birthplace replaces it outright.
  function skyNow() {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const date = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    const time = pad(now.getHours()) + ':' + pad(now.getMinutes());
    let tz = ''; try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { tz = ''; }
    if (!tz) return null;
    const chart = ENGINE.chartFor(date, time, { lat: 45, lon: -now.getTimezoneOffset() / 4, tz: tz, label: '' });
    if (!chart) return null;
    chart.stage = 3; chart.moonKnown = true; chart.anglesKnown = !chart.solarHouses; chart.zoneAssumed = false;
    return chart;
  }
  let skyNowChart = null;
  let heroDrawn = false;
  const heroChart = () => {
    if (current.chart) return current.chart;
    if (!skyNowChart) skyNowChart = skyNow();
    return skyNowChart;
  };

  function renderHeroPrint() {
    if (!heroWheel) return;
    const own = !!current.chart;
    const chart = heroChart();
    drawWheel(chart, heroWheel);
    stripForPrint(heroWheel);
    // the first drawing is a drawing; every later one is a change, and only what moved moves
    heroWheel.classList.toggle('is-redraw', heroDrawn);
    heroDrawn = true;
    heroAnchors = null;                 // the glyphs have moved: the hit test is measured again
    heroClose();
    heroWheel.setAttribute('role', 'img');
    heroWheel.setAttribute('aria-label', !own ? 'The sky as it stands right now.'
      : chart && chart.anglesKnown ? 'Your birth chart, drawn from the details you have entered.'
      : 'Your planets by sign. The houses and the angles are waiting for a birth time.');
  }

  // the plate and the caption follow every keystroke; the wheel only follows a committed change
  /* Their own line, as typed. maxlength caps it at 60 in the browser; this
     trims whitespace so a line of spaces is no line at all, and both plates and
     the counter read the same source. */
  const quoteText = () => (form.elements.quote ? form.elements.quote.value : '').replace(/\s+/g, ' ').trim();

  function renderHeroPlate() {
    if (!heroWheel) return;
    const own = !!current.chart;
    const loc = placeLoc();
    const place = loc ? loc.label : (form.elements.place.value || '').trim();
    const name = (form.elements.name.value || '').trim();
    // the name line stands empty until there is something true to put on it: the
    // place while the name is still unasked, the name itself once it exists
    if (heroPlateName) heroPlateName.textContent = own ? (name || place) : '';
    // the place is already the name line here, so it is not repeated underneath
    if (heroPlateMeta) heroPlateMeta.textContent = plateBits(heroChart(), '').join(' \u00b7 ');
    // their line rides on the print from the first keystroke; empty, it is not there at all
    if (heroPlateQuote) heroPlateQuote.textContent = own ? quoteText() : '';
    if (heroCaption) heroCaption.textContent = own ? 'Your chart, so far. The print is calculated exactly.' : HERO_CAPTION;
    const waiting = own && current.missing.length ? current.missing[0].text : '';
    if (heroAwait) { heroAwait.textContent = waiting; heroAwait.hidden = !waiting; }
  }

  /* ------------------------------------------------- the print, full screen
     A phone gives the print most of the first screen now, but a birth chart at
     390px is still a birth chart read through a letterbox. A tap opens it as
     large as the glass allows. The frame is not copied into the overlay, it is
     MOVED: one chart in the document, so the anchors, the label, the plate and
     the redraw on the next keystroke all keep working untouched, and closing
     puts it back between the same two siblings it came from. */
  const chartFull = $('#chart-full');
  const chartFullClose = $('#chart-full-close');
  const heroFrame = $('.hero__frame');
  const heroPrint = $('.hero__print');
  const phone = window.matchMedia('(max-width: 63.999rem)');
  let fullOpen = false, fullReturn = null;

  function openFull() {
    if (fullOpen || !chartFull || !heroFrame) return;
    fullReturn = heroFrame.nextSibling;
    chartFull.appendChild(heroFrame);
    chartFull.hidden = false;
    document.body.classList.add('is-chart-full');
    fullOpen = true;
    heroClose();                       // a label opened against the small print does not travel
    if (chartFullClose) chartFullClose.focus({ preventScroll: true });
  }
  function closeFull() {
    if (!fullOpen || !heroPrint || !heroFrame) return;
    heroClose();
    heroPrint.insertBefore(heroFrame, fullReturn);
    chartFull.hidden = true;
    document.body.classList.remove('is-chart-full');
    fullOpen = false;
    if (heroWheel) heroWheel.focus({ preventScroll: true });
  }
  if (chartFullClose) chartFullClose.addEventListener('click', closeFull);
  if (chartFull) chartFull.addEventListener('click', ev => { if (ev.target === chartFull) closeFull(); });
  document.addEventListener('keydown', ev => { if (ev.key === 'Escape' && fullOpen) closeFull(); });
  // rotated or resized into the desktop layout: the print belongs back in the hero
  phone.addEventListener('change', ev => { if (!ev.matches) closeFull(); });

  /* ------------------------------------------------- the hero chart, under the finger
     The print answers questions now: hover or tap a planet, an angle or a house
     number and it says what it is, in which sign, to the degree, and in which
     house. Nothing here depends on hitting a glyph. A glyph is 8 to 19 units
     across a 400-unit wheel — six or seven pixels on a phone — so every pointer
     event is resolved against the NEAREST anchor instead: each glyph owns the
     ground around it out to the midpoint between its neighbours, within a reach
     of about 44px. That is a touch target of the right size without a single
     hit shape, and two of them can never overlap and steal from each other.
     The drawing is untouched by any of it: this listens, it does not redraw. */
  const heroTip = $('#hero-tip');
  const heroPanel = $('.hero__print-panel');
  let heroAnchors = null, heroLit = null, heroIdx = -1;

  // every glyph that answers: the planets and nodes, both ends of each angle, the house numbers
  function heroAnchorList() {
    if (heroAnchors) return heroAnchors;
    heroAnchors = [];
    if (!heroWheel) return heroAnchors;
    $$('.pw-planet, .pw-axis, .pw-house', heroWheel).forEach(g => {
      $$('.pw-glyph, .pw-house-num', g).forEach(t => {
        const x = parseFloat(t.getAttribute('x')), y = parseFloat(t.getAttribute('y'));
        if (isFinite(x) && isFinite(y)) heroAnchors.push({ g: g, x: x, y: y - 5 });   // the text y is a baseline; the glyph sits above it
      });
    });
    return heroAnchors;
  }

  // the nearest anchor to a point, in the wheel's own units, or nothing if the
  // point is further from all of them than a fingertip
  function heroHit(clientX, clientY) {
    const r = heroWheel.getBoundingClientRect();
    if (!r.width) return null;
    const perPx = 400 / r.width;
    const x = (clientX - r.left) * perPx, y = (clientY - r.top) * perPx;
    const reach = Math.min(Math.max(22 * perPx, 16), 52);
    let best = null, bestD = Infinity;
    heroAnchorList().forEach(a => {
      const d = Math.hypot(a.x - x, a.y - y);
      if (d < bestD) { bestD = d; best = a; }
    });
    return best && bestD <= reach ? best : null;
  }

  function heroClose() {
    if (heroLit) { heroLit.g.classList.remove('is-lit'); heroLit = null; }
    if (heroTip) heroTip.hidden = true;
  }

  function heroShow(a) {
    if (!a || !heroTip || !heroPanel || !heroWheel) return;
    if (heroLit && heroLit.g !== a.g) heroLit.g.classList.remove('is-lit');
    a.g.classList.add('is-lit');
    heroLit = a;
    const kind = a.g.dataset.kind;
    const glyph = a.g.dataset.glyph ? a.g.dataset.glyph + '\u2009' : '';
    // a planet's detail is its degree, sign and house; a house's is where its cusp falls
    const detail = kind === 'house' ? (a.g.dataset.more || a.g.dataset.detail || '') : (a.g.dataset.detail || '');
    $('.hero__tip-name', heroTip).textContent = glyph + (a.g.dataset.label || '');
    $('.hero__tip-detail', heroTip).textContent = detail;
    heroTip.hidden = false;
    // against the panel, above the glyph, flipped under it at the top edge and kept inside the poster
    const wr = heroWheel.getBoundingClientRect(), pr = heroPanel.getBoundingClientRect();
    const scale = wr.width / 400;
    const cx = wr.left - pr.left + a.x * scale, cy = wr.top - pr.top + a.y * scale;
    const tw = heroTip.offsetWidth, th = heroTip.offsetHeight;
    let top = cy - th - 12 * scale - 4;
    if (top < 2) top = cy + 12 * scale + 4;
    const left = Math.max(4, Math.min(cx - tw / 2, pr.width - tw - 4));
    top = Math.max(2, Math.min(top, pr.height - th - 2));
    heroTip.style.transform = 'translate(' + Math.round(left) + 'px, ' + Math.round(top) + 'px)';
  }

  /* a tap on a body goes to that body's own section of the reading — the row
     that carries its sign, house and what they mean — and opens it. A house
     number has no section of its own, so it says its piece and stays put. */
  function heroJump(g) {
    const kind = g.dataset.kind;
    if (kind === 'house') return;
    const target = kind === 'axis' ? (g.classList.contains('pw-asc') ? 'asc' : 'mc') : g.dataset.id;
    if (!target || !detailsAfter || detailsAfter.hidden) return;
    const row = $('.placements__row[data-target="' + target + '"]');
    if (!row) return;
    closeFull();                                           // the reading is on the page, not over it
    scrollToEl(row, 110);
    if (row.getAttribute('aria-expanded') !== 'true') setExpanded(row, true);
    const twin = wheel ? $('.pw[data-id="' + target + '"]', wheel) : null;
    if (twin) pin(twin);                                   // the screen chart lights the same body
  }

  if (heroWheel && heroTip) {
    heroWheel.setAttribute('tabindex', '0');
    heroWheel.addEventListener('pointermove', ev => {
      if (ev.pointerType === 'touch') return;              // a finger speaks by tapping, not by passing over
      const a = heroHit(ev.clientX, ev.clientY);
      if (a) heroShow(a); else heroClose();
    });
    heroWheel.addEventListener('pointerleave', ev => { if (ev.pointerType !== 'touch') heroClose(); });
    let heroPointer = 'mouse';
    heroWheel.addEventListener('pointerdown', ev => { heroPointer = ev.pointerType || 'mouse'; });
    heroWheel.addEventListener('click', ev => {
      /* on a phone the print leads the page but is still only 390px of it: the
         first tap opens it full screen, and the glyphs answer in there, where
         the label has room and the fingertip is not covering what it asked
         about. A mouse has hover and the desktop has the width: unchanged. */
      if (phone.matches && !fullOpen) { openFull(); return; }
      const a = heroHit(ev.clientX, ev.clientY);
      if (!a) { heroClose(); return; }
      const again = !!(heroLit && heroLit.g === a.g);
      heroShow(a);
      /* a mouse has already read the label by hovering, so its click goes to the
         reading. A finger has not: the first tap names the glyph and the second
         goes to it, or the page would scroll away with the answer still in it. */
      if (heroPointer !== 'touch' || again) heroJump(a.g);
    });
    // the same answers by keyboard: the wheel is one stop, the arrows walk the glyphs
    heroWheel.addEventListener('keydown', ev => {
      const list = heroAnchorList();
      if (!list.length) return;
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') { heroIdx = (heroIdx + 1) % list.length; heroShow(list[heroIdx]); ev.preventDefault(); }
      else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') { heroIdx = (heroIdx - 1 + list.length) % list.length; heroShow(list[heroIdx]); ev.preventDefault(); }
      else if ((ev.key === 'Enter' || ev.key === ' ') && heroLit) { heroJump(heroLit.g); ev.preventDefault(); }
      else if (ev.key === 'Escape') { heroClose(); heroIdx = -1; }
    });
    heroWheel.addEventListener('blur', heroClose);
    // a tap anywhere else puts the label away
    document.addEventListener('pointerdown', ev => {
      if (!heroLit) return;
      if (heroWheel.contains(ev.target) || heroTip.contains(ev.target)) return;
      heroClose();
    }, true);
  }

  /* ------------------------------------------------- the line, and the two ways on
     As soon as the chart is real — a date and a birthplace — one sentence from
     the reading library appears under the print. It is picked, never written
     for the hero, and never patched together: OrreryCompose.hookLine returns a
     whole sentence or nothing at all (tests/hook-line.mjs checks every
     combination). With no birth time it comes from the Sun, and says nothing
     about the time that is missing. From that moment the hero carries both
     paths — the reading and the print — so neither needs scrolling for. */
  const heroHook = $('#hero-hook');
  const heroPaths = $('#hero-paths');
  const heroRead = $('#hero-read');
  const heroCta = $('.hero--midnight .hero__cta');
  const heroMicro = $('.hero--midnight .hero__micro');
  let hookText = null;

  // the chart is real once it has a date and a place; the line waits for that
  function hookFor() {
    if (!current.chart || !committed.dob || !placeLoc()) return '';
    if (!COMPOSE || !COMPOSE.hookLine || !READINGS) return '';
    try { return COMPOSE.hookLine(current.chart, READINGS) || ''; } catch (_) { return ''; }
  }

  const heroSection = $('.hero--midnight');
  function renderHook() {
    if (!heroHook) return;
    const line = hookFor();
    // the print grows on a phone once the chart is theirs: it is a thing to read now, not only to look at
    if (heroSection) heroSection.classList.toggle('has-chart', !!(current.chart && committed.dob && placeLoc()));
    if (line !== hookText) {
      hookText = line;
      heroHook.textContent = line;
      heroHook.classList.remove('is-in');
      if (heroPaths) heroPaths.classList.remove('is-in');
      if (line) requestAnimationFrame(() => requestAnimationFrame(() => {
        heroHook.classList.add('is-in');
        if (heroPaths) heroPaths.classList.add('is-in');   // the line and the way on arrive together
      }));
    }
    heroHook.hidden = !line;
    if (heroPaths) heroPaths.hidden = !line;
    // the caption said what the print is; the line says what the chart says, and two of them under one frame is one too many
    if (heroCaption) heroCaption.hidden = !!line;
    // the two paths take the place of "draw my chart": there is nothing left to draw
    if (heroCta) heroCta.hidden = !!line;
    if (heroMicro) heroMicro.hidden = !!line;
  }

  /* The one place anything pointing at "the reading" lands: its first heading,
     "What the sky was doing." Not the chapter rule above it, not an explanation,
     not the middle of a section. scrollToEl clears the sticky header itself, and
     the extra here is the breathing room under it. */
  function intoReading() {
    const target = $('#chapter-reading') || $('#reading-page');
    if (!target) return;
    scrollToEl(target, 28);
    const title = $('#placements-title');
    if (title) setTimeout(() => title.focus({ preventScroll: true }), 420);
  }

  /* The one action the hero offers, and it is not a gate: it goes to the
     reading. The address that sends the longer reading is asked for at the end
     of that reading, where the visitor has read something and knows what the
     offer is worth — not here, in front of it. */
  if (heroRead) heroRead.addEventListener('click', intoReading);

  /* ------------------------------------------------- the landing
     The chart on the page and the print on the wall are one element. The page
     holds it at reading size, navy on cream, answerable; when the section has
     settled in view it shrinks, hands itself back to its own gold on midnight,
     and the photograph of the framed print comes up around it. The panel lands
     exactly on the poster's printed area, measured once from the photograph, so
     what hangs on that wall is this chart and never a photographed one.
     Transform, opacity and colour only: the page does not reflow. */
  const printStep = $('.print-step');
  const scene = $('#scene');
  const sceneArt = $('#chart-art');
  const sceneToggle = $('#scene-toggle');
  const sceneCaption = $('#scene-caption');
  // the poster's printed area inside assets/hero-poster-book-*.jpg, in the
  // photograph's own pixels (1168 x 880): x 448-720, y 163-567
  const ROOM = { iw: 1168, ih: 880, x: 448, y: 163, w: 272 };
  let landed = false, unframedByHand = false;

  // where that printed area falls inside the scene, given the photograph is
  // laid in with object-fit: cover and object-position: center
  function landingTransform() {
    const bw = scene.clientWidth, bh = scene.clientHeight;
    const k = Math.max(bw / ROOM.iw, bh / ROOM.ih);
    const ox = (bw - ROOM.iw * k) / 2, oy = (bh - ROOM.ih * k) / 2;
    const tx = ox + ROOM.x * k, ty = oy + ROOM.y * k;
    const scale = (ROOM.w * k) / sceneArt.offsetWidth;
    return 'translate(' + (tx - sceneArt.offsetLeft).toFixed(2) + 'px, ' + (ty - sceneArt.offsetTop).toFixed(2) + 'px) scale(' + scale.toFixed(4) + ')';
  }

  function setLanded(on) {
    if (!printStep || !scene || !sceneArt) return;
    landed = on;
    printStep.classList.toggle('is-print', on);
    sceneArt.style.transform = on ? landingTransform() : '';
    // the screen chart's whole palette hangs off .preview__wheel; dropping the
    // class gives the wheel back to the navy-and-gold it was drawn in
    if (wheel) { wheel.classList.toggle('preview__wheel', !on); wheel.classList.toggle('print-wheel', on); }
    if (on) unpin();
    if (sceneToggle) {
      sceneToggle.hidden = false;   // without JS, and before the first landing, there is nothing to toggle
      sceneToggle.textContent = on ? 'See the chart full size' : 'Show it framed on the wall';
      sceneToggle.setAttribute('aria-pressed', String(!on));
    }
    if (sceneCaption) {
      const line = on ? sceneCaption.dataset.print : sceneCaption.dataset.screen;
      if (line) sceneCaption.textContent = line;
    }
  }

  // an empty wheel is not a print: with no chart yet the section keeps the
  // scaffold at reading size and lands the moment there is something to frame
  let wantsLanding = false;
  function maybeLand() {
    if (landed || unframedByHand || !wantsLanding || !current.chart) return;
    setLanded(true);
  }

  if (scene && sceneArt && printStep) {
    if (sceneToggle) {
      sceneToggle.addEventListener('click', () => { if (landed) unframedByHand = true; setLanded(!landed); });
    }
    if (reduceMotion.matches) {
      wantsLanding = true;          // the end of the movement, with none of it
      maybeLand();
    } else if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        entries.forEach(e => { if (e.isIntersecting) { wantsLanding = true; maybeLand(); } });
      }, { threshold: 0.45 }).observe(scene);
    }
    // the chart arrives a beat after the detail that completed it
    ['change', 'input'].forEach(ev => form.addEventListener(ev, () => setTimeout(maybeLand, 80)));
    // the landing is measured in pixels, so it is measured again when they change
    let landTimer = null;
    window.addEventListener('resize', () => {
      if (!landed) return;
      clearTimeout(landTimer);
      landTimer = setTimeout(() => { sceneArt.style.transition = 'none'; sceneArt.style.transform = landingTransform(); requestAnimationFrame(() => { sceneArt.style.transition = ''; }); }, 120);
    });
  }

  /* ---- "See it on your wall": the hero's second button.
     It goes to the photograph, and what has to be true the moment it arrives is
     that the print on that wall is already theirs — not the stock poster, not
     the drawing still shrinking into the frame. So the photograph is decoded,
     the landing is made without its transition while the section is still off
     screen, and only then does the page move. One scroll, nothing on the way. */
  function sceneInView() {
    if (!scene) return false;
    const r = scene.getBoundingClientRect();
    const root = scrollRoot();
    const vh = root ? root.clientHeight : window.innerHeight;
    return r.bottom > 0 && r.top < vh;
  }
  function scrollToScene() {
    const root = scrollRoot();
    const cur = root ? root.scrollTop : window.scrollY;
    const vh = root ? root.clientHeight : window.innerHeight;
    const top = (headerEl ? headerEl.offsetHeight : 0) + 12;
    const r = scene.getBoundingClientRect();
    // centred in what is left of the screen under the header; when the frame is
    // taller than that, its top sits just under the header instead of centring,
    // so nothing above it is ever cut off
    const slack = Math.max(0, (vh - top - r.height) / 2);
    (root || window).scrollTo({ top: Math.max(0, r.top + cur - top - slack), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }
  async function seeItOnYourWall() {
    if (!current.chart) { goDraw(); return; }       // nothing to hang yet
    if (!scene || !printStep || !sceneArt) { openFormats(); return; }
    // the photograph is lazy: unasked, the wall would arrive after the scroll did
    const roomImg = $('.scene__room-img');
    if (roomImg && !roomImg.complete) {
      roomImg.setAttribute('loading', 'eager');
      try { await roomImg.decode(); } catch (_) {}
    }
    unframedByHand = false;                          // they have just asked for the wall
    wantsLanding = true;
    if (!landed) {
      if (sceneInView()) setLanded(true);            // in view: let it travel, they can see it
      else {                                         // out of view: nothing to watch, so land it outright
        const prev = sceneArt.style.transition;
        sceneArt.style.transition = 'none';
        setLanded(true);
        void sceneArt.offsetWidth;                   // commit it before the transition comes back
        sceneArt.style.transition = prev;
      }
    }
    scrollToScene();
  }
  $$('[data-wall-cta]').forEach(btn => btn.addEventListener('click', seeItOnYourWall));

  narrow.addEventListener('change', () => { updatePreview.lastKey = null; updatePreview(); });

  const readingBody = $('#reading-page-body'), readingTitle = $('#reading-page-title'), readingMissing = $('#reading-missing');
  /* The panel's own element. It used to be `readingSection`, which is also the
     jump link's target — and when that target was retargeted from #reading-page
     to the chapter heading above it, this went with it silently: the ready flag
     landed on a <header> no stylesheet reads, .reading--page stayed at opacity 0
     and the reading was composed, in the DOM, and invisible. Separate handles. */
  const readingPanel = $('#reading-page');
  function renderPlacements(chart) {
    if (!placements) return;
    while (placements.firstChild) placements.removeChild(placements.firstChild);
    if (!chart) { if (previewPhase) previewPhase.textContent = ''; renderReading(null); return; }
    const full = chart.stage === 3, timed = chart.stage >= 2;
    // "☉ Sun in Sagittarius      10° 26′ · 10th house  more": a button; its reading opens beneath
    const row = (target, glyph, name, sign, pos, retro, segs) => {
      const li = document.createElement('li');
      li.className = 'placements__item';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'placements__row';
      btn.dataset.target = target;
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', 'placement-more-' + target);
      const g = document.createElement('span'); g.className = 'placements__glyph'; g.textContent = glyph; g.setAttribute('aria-hidden', 'true');
      const n = document.createElement('span'); n.className = 'placements__name';
      n.append(name, ' ');
      const sg = document.createElement('span'); sg.className = 'placements__sign'; sg.textContent = 'in ' + sign; n.appendChild(sg);
      const p = document.createElement('span'); p.className = 'placements__pos';
      p.textContent = pos;
      if (retro) { const r = document.createElement('span'); r.className = 'placements__retro'; r.textContent = '℞'; r.setAttribute('aria-label', 'retrograde'); r.title = 'retrograde'; p.prepend(r, ' '); }
      const more = document.createElement('span'); more.className = 'placements__more'; more.setAttribute('aria-hidden', 'true'); more.textContent = 'more';
      btn.append(g, n, p, more);
      const panel = document.createElement('div');
      panel.className = 'placements__panel';
      panel.id = 'placement-more-' + target;
      panel.hidden = true;
      const inner = document.createElement('div'); inner.className = 'placements__panel-inner';
      renderParas(inner, [segs]);
      panel.appendChild(inner);
      li.append(btn, panel);
      placements.appendChild(li);
    };
    // a placement that cannot be placed yet: one quiet line saying what it is waiting for
    const held = (glyph, name, why) => {
      const li = document.createElement('li');
      li.className = 'placements__item placements__item--held';
      const g = document.createElement('span'); g.className = 'placements__glyph'; g.textContent = glyph; g.setAttribute('aria-hidden', 'true');
      const n = document.createElement('span'); n.className = 'placements__name'; n.textContent = name;
      const w = document.createElement('span'); w.className = 'placements__held'; w.textContent = why;
      li.append(g, n, w);
      placements.appendChild(li);
    };
    const needTime = current.missing.length ? current.missing[0].text : '';
    const bodyRow = i => {
      const b = chart.bodies[i], f = fmtDeg(b.lon);
      if (b.key === 'moon' && !chart.moonKnown) { held(b.glyph, 'Moon', 'Moves about thirteen degrees a day: it needs your birth time.'); return; }
      // by sign alone until the time is known; then to the arcminute; the house once the place is known
      // a body within 0.01°/day of a station is called stationary, not retrograde or direct
      const pos = (full ? f.text + ' · ' + houseLabel(chart.cusps, b.lon) : timed ? f.text : 'by sign') + (b.stationary ? ' · stationary' : '');
      // within a degree of a house cusp: said on the row, so an approximate birth time is never presented as certain
      const nearFlag = (current.analysis && current.analysis.nearCusp || []).find(n => n.kind === 'house' && n.body.key === b.key);
      row('p' + i, b.glyph, b.name + (b.node ? ' (true)' : ''), f.sign, pos + (nearFlag ? ' · within 1° of a cusp' : ''), b.retro && !b.node && !b.stationary, rowReading(chart, b));
    };
    // the big three first: Sun, Moon, then the Ascendant on its own row; the Midheaven closes the list
    bodyRow(0); bodyRow(1);
    if (full && chart.solarHouses) held('ASC', 'Ascendant', 'No birth time: the horizon cannot be placed. The houses are solar, counted from the Sun.');
    else if (full) {
      const fa = fmtDeg(chart.asc);
      row('asc', 'ASC', 'Ascendant', fa.sign, fa.text, false, rowReading(chart, { axis: 'asc', lon: chart.asc }));
    } else held('ASC', 'Ascendant', timed ? 'The horizon needs your birthplace.' : 'The horizon needs your birth time and place.');
    for (let i = 2; i < chart.bodies.length; i++) bodyRow(i);
    if (full && chart.solarHouses) held('MC', 'Midheaven', 'No birth time: the meridian cannot be placed.');
    else if (full) {
      const fm = fmtDeg(chart.mc);
      row('mc', 'MC', 'Midheaven', fm.sign, fm.text, false, rowReading(chart, { axis: 'mc', lon: chart.mc }));
    } else held('MC', 'Midheaven', timed ? 'The meridian needs your birthplace.' : 'The meridian needs your birth time and place.');
    const ascEl = $('.pw-asc', wheel), mcEl = $('.pw-mc', wheel);
    if (ascEl) ascEl.dataset.id = 'asc';
    if (mcEl) mcEl.dataset.id = 'mc';
    if (previewPhase) previewPhase.textContent = chart.moonKnown ? phaseLabel(chart.lunarPhase) : '';
    renderReading(chart);
  }
  // tier two, beneath the analysis: the visitor's Sun, Moon and rising, once all three can be placed
  /* ---- "What this means for you"
     Every analysis section explains its concept for free. This is the other
     half — what that concept says about this chart — and it comes from
     OrreryCompose.sectionReading, which returns nothing rather than anything
     unwritten. A section with nothing of its own never shows the option at all,
     so there is no locked door with nothing behind it.

     The gate is the reading's gate. Same flag, same device memory, same outbox:
     an address given here opens the reading too, and an address already given
     opens these with no asking. It is one gate with two doors, never two. */
  const sectionPicks = new Set();
  let gateHost = null;

  function sectionBodyFor(key) {
    if (!current.chart || current.stage < 3 || !COMPOSE.sectionReading) return null;
    try { return COMPOSE.sectionReading(current.chart, READINGS, current.analysis, key); }
    catch (_) { return null; }
  }

  function closeGate() {
    if (gateHost) { gateHost.hidden = true; gateHost = null; }
  }

  /* one field, inside the section that asked. Never a dialog, and never a
     second time: once it is given every section opens for the rest of the
     visit and on the next visit to this device. */
  function openGate(sec, detailsEl) {
    const host = $('.an__gate', sec);
    if (!host) return;
    if (gateHost && gateHost !== host) gateHost.hidden = true;
    gateHost = host;
    host.hidden = false;
    paintGatePick();
    const field = $('.an__gate-email', host);
    // the field must not open under a phone keyboard: put it in the middle of the screen first
    scrollToEl(host, 140);
    if (field) setTimeout(() => field.focus({ preventScroll: true }), 420);
    if (detailsEl) detailsEl.open = false;
  }

  function paintGatePick() {
    $$('.an__gate').forEach(host => {
      const n = sectionPicks.size;
      const all = $('.an__gate-all', host), some = $('.an__gate-some', host), someLabel = $('.an__gate-some-label', host);
      if (!all || !some) return;
      some.disabled = !n;
      if (!n) all.checked = true;
      if (someLabel) someLabel.textContent = n ? (n === 1 ? 'Just the one I ticked' : 'Just the ' + n + ' I ticked') : 'Nothing ticked yet';
    });
  }

  function renderSectionMine() {
    $$('.an__mine').forEach(det => {
      const key = det.dataset.mine, sec = det.closest('.an');
      const paras = sectionBodyFor(key);
      const pick = sec ? $('.an__pick', sec) : null;
      if (!paras) { det.hidden = true; det.open = false; if (pick) pick.hidden = true; sectionPicks.delete(key); return; }
      det.hidden = false;
      if (pick) pick.hidden = false;
      const body = $('.an__mine-body', det);
      if (body && det.dataset.done !== 'yes') { renderParas(body, paras); det.dataset.done = 'yes'; }
      if (body && det.dataset.chart !== updatePreview.lastKey) { renderParas(body, paras); det.dataset.chart = updatePreview.lastKey || ''; }
    });
    paintGatePick();
  }

  // the click is intercepted only while it is locked; unlocked it is an ordinary <details>
  document.addEventListener('click', ev => {
    const sum = ev.target.closest && ev.target.closest('.an__mine > summary');
    if (!sum) return;
    if (unlocked) return;
    const det = sum.parentElement, sec = det.closest('.an');
    ev.preventDefault();
    openGate(sec, det);
  });
  document.addEventListener('change', ev => {
    const box = ev.target.closest && ev.target.closest('[data-pick]');
    if (box) {
      if (box.checked) sectionPicks.add(box.dataset.pick); else sectionPicks.delete(box.dataset.pick);
      if (sectionPicks.size) { $$('.an__gate-some').forEach(r => { r.disabled = false; r.checked = true; }); }
      paintGatePick();
      return;
    }
    const gateBox = ev.target.closest && ev.target.closest('.an__gate-all, .an__gate-some');
    if (gateBox) paintGatePick();
  });

  document.addEventListener('click', ev => {
    const btn = ev.target.closest && ev.target.closest('.an__gate-send');
    if (!btn) return;
    const host = btn.closest('.an__gate'), field = $('.an__gate-email', host);
    const email = (field.value || '').trim();
    if (!okEmail(email)) { setError(field, 'Enter your email and we will send your reading.'); field.focus({ preventScroll: true }); return; }
    setError(field, '');
    const some = $('.an__gate-some', host);
    const picks = some && some.checked && sectionPicks.size ? Array.from(sectionPicks) : [];
    unlockReading(email);          // the reading and every section, here and on the next visit
    queueAndSend(email, picks);
  });

  /* ---- the reading, in two halves
     The page carries the opening of the Sun, Moon and rising — about a third,
     whole sentences, stopping inside a paragraph rather than at the end of one,
     so it stops while it is still saying something. The rest, and the love
     sections with it, wait behind one address. Every heading is written either
     way: what is waiting is named, never blurred, never counted down. */
  const readingLocked = $('#reading-locked');
  const FREE_SHARE = 1 / 3;

  // the headings the page reading's paragraphs answer to, in the order compose() returns them
  function readingHeads(chart) {
    const FACTS = window.OrreryFacts, facts = FACTS.factsFor(chart, READINGS);
    const heads = ['The Sun in ' + FACTS.signOf(chart.bodies[0].lon), 'The Moon in ' + FACTS.signOf(chart.bodies[1].lon)];
    if (facts.rising) heads.push(facts.rising + ' rising');
    heads.push('The three together');
    return heads;
  }

  /* whole sentences up to the budget, then stop wherever that falls. The
     paragraph the cut lands in keeps its heading and carries its own remainder. */
  function splitReading(chart) {
    const paras = pageReading(chart).map(segs => segs.map(s => s.text).join(' ').trim()).filter(Boolean);
    const heads = readingHeads(chart);
    const budget = Math.round(paras.reduce((n, t) => n + t.length, 0) * FREE_SHARE);
    const free = [], rest = [];
    let used = 0, cut = false;
    paras.forEach((text, i) => {
      if (cut) { rest.push({ heading: heads[i] || '', text: text }); return; }
      const sentences = text.match(/[^.!?]+[.!?]+["'’)\]]*\s*/g) || [text];
      let take = '';
      for (const s of sentences) { if (take && used + take.length >= budget) break; take += s; }
      const tail = text.slice(take.length).trim();
      if (take.trim()) free.push(take.trim());
      used += take.length;
      if (used >= budget) { cut = true; if (tail) rest.push({ heading: heads[i] || '', text: tail }); }
    });
    return { free, rest };
  }

  // the rest of the reading, grouped: what is left of the three, then love and what follows it
  function lockedGroups(chart, rest) {
    const groups = [];
    if (rest.length) groups.push({ title: '', sections: rest.map(r => ({ heading: r.heading, paras: [r.text] })) });
    let more = [];
    try { more = COMPOSE.emailTwoSections(chart, READINGS, current.analysis) || []; } catch (_) { more = []; }
    more.forEach(g => groups.push({
      title: g.title,
      sections: (g.sections || []).map(s => ({ heading: s.heading, paras: (s.paras || []).map(segs => segs.map(x => x.text).join(' ').trim()).filter(Boolean) }))
    }));
    return groups;
  }

  function renderLocked(chart, rest) {
    if (!readingLocked) return;
    while (readingLocked.firstChild) readingLocked.removeChild(readingLocked.firstChild);
    if (!chart) return;
    lockedGroups(chart, rest).forEach(g => {
      const wrap = el('div', 'reading-locked__group');
      if (g.title) wrap.appendChild(el('p', 'reading-locked__title', g.title));
      g.sections.forEach(sec => {
        if (!sec.paras.length) return;
        // a section with no heading has nothing to show while it is locked: it
        // would be an empty gap standing in for text, which is worse than absence
        if (!sec.heading && !unlocked) return;
        const box = el('div', 'reading-locked__sec');
        if (sec.heading) box.appendChild(el('p', 'reading-locked__head', sec.heading));
        const body = el('div', 'reading-locked__body');
        body.hidden = !unlocked;
        sec.paras.forEach(t => body.appendChild(el('p', null, t)));
        box.appendChild(body);
        wrap.appendChild(box);
      });
      if (wrap.children.length) readingLocked.appendChild(wrap);
    });
    readingLocked.classList.toggle('is-unlocked', unlocked);
  }

  function renderReading(chart) {
    if (!readingBody) return;
    const ready = !!(chart && chart.stage === 3);
    if (readingPanel) readingPanel.dataset.state = ready ? 'ready' : 'pending';
    if (readingMissing) { readingMissing.hidden = ready; readingMissing.textContent = ready ? '' : (chart ? ANALYSIS.waitingFor(chart, 'your Sun, Moon and rising read together', true) : ''); }
    while (readingBody.firstChild) readingBody.removeChild(readingBody.firstChild);
    if (!ready) { renderLocked(null, []); if (readingOffer) readingOffer.hidden = true; return; }
    const split = splitReading(chart);
    // unlocked, the page carries the whole thing and the cut is not made at all
    const shown = unlocked ? pageReading(chart).map(segs => segs.map(s => s.text).join(' ').trim()).filter(Boolean) : split.free;
    shown.forEach(t => readingBody.appendChild(el('p', null, t)));
    renderLocked(chart, unlocked ? [] : split.rest);
    if (readingOffer) readingOffer.hidden = unlocked;
  }

  /* One address, once. The text opens before the network is touched — the
     address is not a receipt to wait for — and the device remembers, so a
     second chart is never asked again. */
  function unlockReading(email) {
    try { localStorage.setItem(UNLOCK_KEY, email || '1'); } catch (_) {}
    unlocked = true;
    closeGate();
    renderReading(current.chart);
    syncChat();
  }

  /* ----------------------------------------------------------- the analysis sections
     Facts first, then patterns; the reading follows. Every figure comes from
     assets/orrery-analysis.js; this only lays it out. A section whose inputs are
     not there yet keeps its heading and says, in one line, what it is waiting for. */
  const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
  const HOUSE_LABEL = ['Self & body', 'Money & worth', 'Mind & words', 'Home & roots', 'Play & making', 'Work & health', 'Partnership', 'Shared & hidden', 'Belief & far travel', 'Vocation & reputation', 'Friends & causes', 'Retreat & the unseen'];
  const QUADRANT_WORDS = ['the lower-left quarter, read for the self and its immediate world', 'the lower-right quarter, read for home, work and the early years', 'the upper-right quarter, read for other people and the wider world', 'the upper-left quarter, read for public life and what is withdrawn from it'];
  const PATTERN_WORDS = {
    'stellium': 'A stellium concentrates a chart: instead of a spread of separate concerns, most of the weight lands in one place and one register. Everything here has to share a room.',
    'grand trine': 'Three planets each a third of the circle apart, all in one element: a closed circuit where things flow easily and, left alone, go nowhere. Talent that has to be picked up to be used.',
    'T-square': 'An opposition with a third planet squaring both ends: two forces pulling against each other and one caught between them. The apex is where the pressure comes out, usually as work.',
    'grand cross': 'Two oppositions at right angles, four planets in a square. The most tense figure a chart can hold and, in people who carry one, a source of unusual stamina.',
    'yod': 'Two planets in sextile, both quincunx a third: the finger of God, in the old phrase. The apex planet is asked to do something the rest of the chart cannot quite explain.'
  };
  const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
  const dm = deg => { let d = Math.floor(deg), m = Math.round((deg - d) * 60); if (m === 60) { d += 1; m = 0; } return d + '° ' + String(m).padStart(2, '0') + '′'; };
  const signGlyph = lon => SIGN_GLYPHS[Math.floor(norm(lon) / 30)] + '︎';
  const posText = lon => { const f = fmtDeg(lon); return f.sign + ' ' + f.text; };
  const listNames = names => names.length < 2 ? names.join('') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  const ordWord = n => ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth'][n];
  const numWord = n => ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'][n] || String(n);
  const glyphSpan = (glyph, label) => { const sp = el('span', 'an-glyph', glyph); if (label) sp.setAttribute('aria-label', label); else sp.setAttribute('aria-hidden', 'true'); return sp; };
  const bodyGlyph = b => b.angle ? glyphSpan(b.short || b.glyph, b.name) : glyphSpan(b.glyph);

  function setSection(id, ready, missingText) {
    // a section that is not ready keeps its heading and one line; the body is emptied, so nothing of an earlier chart lingers
    const sec = $('#an-' + id);
    if (!sec) return null;
    sec.dataset.state = ready ? 'ready' : 'pending';
    const miss = $('.an__missing', sec), body = $('.an__body', sec), note = $('.an__note', sec);
    miss.hidden = ready; miss.textContent = ready ? '' : missingText;
    while (body.firstChild) body.removeChild(body.firstChild);
    note.textContent = ''; note.hidden = true;
    return { sec, body, note: t => { note.textContent = t; note.hidden = !t; } };
  }
  // what a held section is waiting for, in the visitor's own terms
  const waitingFor = (what, needsPlace) => ANALYSIS.waitingFor(current.chart, what, needsPlace);
  // the Moon as a disc: dark, with the lit part in gold on the side the light comes from
  // (the right while waxing, the left while waning, as seen from the northern hemisphere)
  function moonDisc(m) {
    const r = 40, e = m.waxing ? m.elongation : 360 - m.elongation, L = m.waxing ? 1 : -1, k = Math.cos(e * RAD);
    const svg = node('svg', { viewBox: '-50 -50 100 100', class: 'moon-disc', 'aria-hidden': 'true', focusable: 'false' });
    svg.appendChild(node('circle', { cx: 0, cy: 0, r: r + 3, fill: 'none', stroke: 'var(--gold)', 'stroke-width': 1, opacity: 0.6 }));
    svg.appendChild(node('circle', { cx: 0, cy: 0, r: r, fill: 'var(--navy)' }));
    if (m.illuminated > 0.005) {
      // the lit region lies between the disc's edge on the lit side and the terminator, an ellipse of half-width r·cos(e)
      const pts = [];
      for (let i = 0; i <= 48; i++) { const y = -r + (2 * r * i) / 48, w = Math.sqrt(Math.max(0, r * r - y * y)); pts.push((L * w).toFixed(2) + ',' + y.toFixed(2)); }
      for (let i = 48; i >= 0; i--) { const y = -r + (2 * r * i) / 48, w = Math.sqrt(Math.max(0, r * r - y * y)); pts.push((L * w * k).toFixed(2) + ',' + y.toFixed(2)); }
      svg.appendChild(node('polygon', { points: pts.join(' '), fill: 'var(--gold)' }));
    }
    return svg;
  }

  /* ----------------------------------------------------------- the contents
     A quiet index of what follows, built from the sections that exist. A
     section the birth data does not yet allow is dimmed and says what it
     needs, not hidden: seeing what is missing is the case for finding the
     birth time. The numeral and the name are the link; landing puts focus on
     the section's heading. */
  const contentsList = $('#contents .contents__list');
  const ROMAN_ROWS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
  function renderContents(chart) {
    if (!contentsList) return;
    const timed = !!(chart && chart.stage >= 2), full = !!(chart && chart.stage === 3);
    const needTime = 'Needs your birth time', needPlace = timed ? 'Needs your birthplace' : 'Needs your birth time and place';
    const rows = [
      { id: 'placements-title', name: 'Your placements', desc: 'Every position, exactly', ok: true },
      { id: 'an-angles-title', name: 'Beyond the planets', desc: full ? 'Rising sign, Midheaven, nodes' : 'The nodes now. ' + needPlace + ' for the rising sign and Midheaven', ok: true },
      { id: 'an-aspects-title', name: 'Aspects', desc: 'How the planets speak to each other', ok: timed, need: needTime },
      { id: 'an-houses-title', name: 'Houses', desc: 'Where the weight of your life falls', ok: full, need: needPlace },
      { id: 'an-patterns-title', name: 'Patterns', desc: 'The shapes your chart makes', ok: timed, need: needTime },
      { id: 'an-signature-title', name: 'Your signature', desc: 'What the chart is mostly made of', ok: timed, need: needTime },
      { id: 'reading-page-title', name: 'The reading', desc: 'Your Sun, Moon and rising, written', ok: full, need: needPlace }
    ].filter(r => document.getElementById(r.id));
    while (contentsList.firstChild) contentsList.removeChild(contentsList.firstChild);
    rows.forEach((r, i) => {
      const li = el('li', 'contents__row' + (r.ok ? '' : ' is-held'));
      const a = el('a', 'contents__link'); a.href = '#' + r.id;
      a.appendChild(el('span', 'contents__num', ROMAN_ROWS[i]));
      a.appendChild(el('span', 'contents__name', r.name));
      a.appendChild(el('span', 'contents__desc', r.ok ? r.desc : r.need));
      a.addEventListener('click', ev => {
        ev.preventDefault();
        const target = document.getElementById(r.id);
        if (!target) return;
        scrollToEl(target, 24);
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
      li.appendChild(a);
      contentsList.appendChild(li);
    });
  }

  function renderAnalysis(chart, A) {
    if (!$('#analysis')) return;
    const timed = !!(chart && chart.stage >= 2), full = !!(chart && chart.stage === 3);
    // I — the moon that night: needs the time
    let S = setSection('moon', timed, waitingFor('the Moon that night'));
    if (S && timed) {
      const m = A.moon, panel = el('div', 'moon-panel');
      const fig = el('div', 'moon-panel__fig'); fig.appendChild(moonDisc(m));
      const facts = el('div', 'moon-panel__facts');
      facts.appendChild(el('p', 'moon-panel__name', m.name));
      const dl = el('p', 'moon-panel__data');
      dl.append('Day ' + m.day.toFixed(1) + ' of ' + m.month.toFixed(2), document.createElement('br'), (m.illuminated * 100).toFixed(1) + '% illuminated', document.createElement('br'), 'Elongation ' + dm(m.elongation));
      facts.appendChild(dl);
      const prose = el('div', 'moon-panel__prose');
      renderParas(prose, [[COMPOSE_ENTRY('moonpanel.' + m.name, '50–60', chart)]]);
      panel.append(fig, facts, prose);
      S.body.appendChild(panel);
      S.note('Phase is computed from the angle between the Sun and the Moon at your birth, not from the calendar date.' + (chart.zoneAssumed ? ' Until a birthplace is entered your time zone is taken from this device; the place corrects it.' : ''));
    }
    // II — beyond the planets: the angles need the whole chart; the nodes only the date
    S = setSection('angles', !!chart, '');
    if (S && chart) {
      const grid = el('ul', 'points'); grid.setAttribute('aria-label', 'Angles and points');
      const nn = chart.bodies.find(b => b.key === 'north node'), sn = chart.bodies.find(b => b.key === 'south node');
      const item = (glyph, name, def, lon, heldText) => {
        const li = el('li', 'points__item' + (heldText ? ' is-held' : ''));
        li.appendChild(glyphSpan(glyph));
        const txt = el('div', 'points__text'); txt.appendChild(el('b', 'points__name', name)); txt.appendChild(el('span', 'points__def', def)); li.appendChild(txt);
        const val = el('span', 'points__pos');
        if (heldText) val.textContent = heldText; else { val.appendChild(glyphSpan(signGlyph(lon))); val.append(' ' + posText(lon)); }
        li.appendChild(val);
        grid.appendChild(li);
      };
      const need = chart.solarHouses ? 'needs the birth time' : timed ? 'needs your birthplace' : 'needs your birth time and place';
      const angled = full && !chart.solarHouses;
      item('AC', 'Ascendant', 'The sign rising in the east: how you come across', chart.asc, angled ? '' : need);
      item('MC', 'Midheaven', 'The point straight overhead: your work and public name', chart.mc, angled ? '' : need);
      item(nn.glyph, 'North Node', 'Where the Moon\'s path crosses the Sun\'s, heading north: where you are headed', nn.lon, '');
      item(sn.glyph, 'South Node', 'The opposite crossing: what you came in knowing', sn.lon, '');
      if (chart.chiron) item(chart.chiron.glyph, 'Chiron', 'A small body between Saturn and Uranus, on a fifty-year orbit' + (chart.chiron.stationary ? '. Almost still on your day' : chart.chiron.retro ? '. Retrograde on your day: from Earth it looked to be moving backwards' : ''), chart.chiron.lon, '');
      S.body.appendChild(grid);
      const nodeMotion = nn.stationary ? 'all but stationary, at the turn between the two.' : nn.retro ? 'backwards, as it usually does.' : 'forward, which it does for only a few days in each month.';
      S.note('We use the true node, the node\'s actual position on your date. The mean node is a smoothed average and travels backwards without exception; the true node wobbles either side of it and turns direct for short stretches. On your day it was moving ' + nodeMotion + (chart.solarHouses ? ' No birth time was given, so the Ascendant and Midheaven cannot be placed: this chart is drawn for sunrise with solar houses.' : full ? '' : timed ? ' The Ascendant and Midheaven need your birthplace.' : ' The Ascendant and Midheaven need your birth time and place.'));
    }
    // III — every aspect, tightest first: between planets from the time; to the angles from the place
    S = setSection('aspects', timed, waitingFor('your aspects'));
    if (S && timed) {
      const legend = el('p', 'asp-legend');
      [['hard', '☌ □ ☍', 'Hard: conjunction, square, opposition'], ['soft', '△ ⚹', 'Soft: trine, sextile'], ['quincunx', '⚻', 'Quincunx: neither of the two']].forEach(([cls, g, t]) => {
        const sp = el('span', 'asp-legend__item asp--' + cls); sp.appendChild(glyphSpan(g)); sp.append(' ' + t); legend.appendChild(sp);
      });
      S.body.appendChild(legend);
      const list = el('ol', 'asp-list'); list.setAttribute('aria-label', 'Aspects, tightest first');
      A.aspects.forEach(a => {
        const li = el('li', 'asp asp--' + a.cls);
        li.appendChild(glyphSpan(a.glyph, a.type));
        const pair = el('span', 'asp__pair');
        // a planet shows glyph and name; an angle its short label alone, as on the wheel
        const side = pt => { if (pt.angle) pair.appendChild(glyphSpan(pt.short, pt.name)); else { pair.appendChild(bodyGlyph(pt)); pair.append(' ' + pt.name); } };
        side(a.a); pair.append(' '); pair.appendChild(el('em', null, a.type)); pair.append(' '); side(a.b);
        li.appendChild(pair);
        li.appendChild(el('span', 'asp__orb', dm(a.orb)));
        list.appendChild(li);
      });
      S.body.appendChild(list);
      const un = A.signature.unaspected;
      S.note(cap(numWord(A.aspects.length)) + ' aspect' + (A.aspects.length === 1 ? '' : 's') + ' inside eight degrees (the quincunx inside three), counted between the ten planets' + (full ? ' and the two angles, never angle to angle.' : '. Aspects to the Ascendant and Midheaven need your birthplace.') +
        (un.length ? ' ' + listNames(un.map(b => b.name)) + (un.length === 1 ? ' makes none of them, which is the first thing worth saying about this chart; see the signature below.' : ' make none of them; see the signature below.') : ''));
    }
    // IV — the twelve houses
    S = setSection('houses', full, waitingFor('your houses', true));
    if (S && full) {
      const H = A.houses;
      const subHead = el('p', 'an__sub', 'The four angular houses');
      S.body.appendChild(subHead);
      const list = el('ol', 'hs-list'); list.setAttribute('aria-label', 'The twelve houses');
      H.forEach(h => {
        const li = el('li', 'hs' + (h.angular ? ' hs--angular' : '')); if (!h.angular) li.hidden = true;
        li.appendChild(el('span', 'hs__num', ROMAN[h.n - 1]));
        const txt = el('div', 'hs__text');
        txt.appendChild(el('b', 'hs__label', HOUSE_LABEL[h.n - 1]));
        const names = h.planets.map(p => p.name);
        let sub = names.length ? listNames(names) : 'No planets: read through ' + SIGN_NAMES[h.sign] + ' and its ruler';
        if (h.n === 1 && names.length) sub += ', with the Ascendant'; if (h.n === 10 && names.length) sub += ', with the Midheaven';
        if (h.nodes.length) sub += (names.length ? '; the ' : '. The ') + listNames(h.nodes.map(n => n.name)) + (h.nodes.length > 1 ? ' sit' : ' sits') + ' here too';
        txt.appendChild(el('span', 'hs__sub', sub)); li.appendChild(txt);
        const val = el('span', 'hs__cusp'); val.appendChild(glyphSpan(signGlyph(h.cusp))); val.append(' ' + fmtDeg(h.cusp).text);
        if (h.planets.length) { const gl = el('span', 'hs__planets'); h.planets.forEach(p => gl.appendChild(glyphSpan(p.glyph, p.name))); val.appendChild(gl); }
        li.appendChild(val);
        list.appendChild(li);
      });
      S.body.appendChild(list);
      const btn = el('button', 'btn btn--ghost btn--small hs-toggle', 'Show all twelve'); btn.type = 'button'; btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open)); btn.textContent = open ? 'Show the four angular houses' : 'Show all twelve';
        $$('.hs:not(.hs--angular)', list).forEach(li => { li.hidden = !open; });
        subHead.textContent = open ? 'All twelve houses' : 'The four angular houses';
      });
      S.body.appendChild(btn);
      const empty = H.filter(h => !h.planets.length);
      /* The houses blurb above says we use Placidus, and for all but a handful
         of birthplaces that is true. Inside the polar circles it is not: the
         Placidus method divides the arc a degree travels between rising and
         culminating, and up there some degrees never rise, so there is no arc
         and no answer — Swiss Ephemeris itself declines to give one. The engine
         falls back to Porphyry there, as Swiss Ephemeris and astro.com do, and
         this says so rather than letting the blurb be quietly wrong for
         somebody born in Tromso. */
      S.note((chart.houseSystem === 'Porphyry' ? 'You were born inside the polar circle, where the Placidus houses named above cannot be drawn: some degrees of the zodiac never rise there, so there is no rising-to-culminating arc to divide. These are Porphyry houses instead, which cut each quarter of the sky in three. It is the same fallback Swiss Ephemeris and astro.com use, and the Ascendant and Midheaven are unaffected. ' : '') +
        (chart.solarHouses ? 'No birth time was given, so these are solar houses: twelve equal houses counted from the Sun, not from the horizon. A birth time would replace them with the Placidus houses. ' : '') +
        (empty.length ? cap(numWord(empty.length)) + ' of your houses hold no planets: the ' + listNames(empty.map(h => ordWord(h.n))) + '. That is ordinary arithmetic; ten bodies cannot fill twelve rooms, so at least two houses are always empty. ' : 'Every house but two would need a planet of its own to be full, and yours come unusually close. ') +
        'An empty house is read through the sign on its cusp and the planet that rules that sign, and it is not a gap in a life.');
    }
    // V — patterns in the whole: from the time
    S = setSection('patterns', timed, waitingFor('the patterns in your chart'));
    if (S && timed) {
      const P = A.patterns;
      P.found.forEach((f, i) => {
        const card = el('div', 'pat' + (i === 0 ? ' pat--lead' : ''));
        const head = el('p', 'pat__head');
        const title = f.kind === 'stellium' ? SIGN_NAMES[f.sign] + ' stellium' : cap(f.kind) + (f.element ? ' in ' + f.element.toLowerCase() : f.modality ? ' in ' + f.modality.toLowerCase() + ' signs' : '');
        head.appendChild(el('b', null, title)); head.append(' ');
        const gl = el('span', 'pat__glyphs'); f.bodies.forEach(b => gl.appendChild(glyphSpan(b.glyph, b.name))); head.appendChild(gl);
        let detail = listNames(f.bodies.map(b => b.name));
        if (f.kind === 'stellium') detail += ': spread ' + dm(f.spread) + (f.houses && f.houses.length === 1 ? ', all in the ' + ordWord(f.houses[0]) + ' house' : f.houses ? ', across the ' + listNames(f.houses.map(ordWord)) : '');
        if (f.apex) detail += ', apex ' + f.apex.name;
        head.appendChild(el('span', 'pat__detail', detail));
        card.appendChild(head);
        card.appendChild(el('p', 'pat__body', PATTERN_WORDS[f.kind]));
        S.body.appendChild(card);
      });
      if (P.absent.length) {
        const box = el('div', 'pat pat--absent');
        box.appendChild(el('p', 'pat__head', P.found.length ? 'Not present in this chart' : 'None of the classic figures is present in this chart'));
        const grid = el('dl', 'pat__grid');
        P.absent.forEach(a => { const item = el('div', 'pat__absent'); item.appendChild(el('dt', null, cap(a.kind))); item.appendChild(el('dd', null, a.why)); grid.appendChild(item); });
        box.appendChild(grid);
        S.body.appendChild(box);
      }
    }
    // VI — the signature: the counts from the time; the quadrants from the place
    S = setSection('signature', timed, waitingFor('the signature'));
    if (S && timed) {
      const G = A.signature;
      S.body.appendChild(el('p', 'sig-count', 'What we count. The ten planets, with the Sun and Moon included as is conventional. Nothing else: the Ascendant and Midheaven are directions, not bodies, and the nodes are intersections. Ten bodies, ten tallies, and the columns below add to ten.'));
      const bars = (title, groups, order) => {
        const wrap = el('div', 'sig-bars'); wrap.appendChild(el('p', 'an__sub', title));
        order.forEach(k => {
          const row = el('div', 'sig-bar'); row.appendChild(el('span', 'sig-bar__label', k));
          const track = el('span', 'sig-bar__track'); const fill = el('span', 'sig-bar__fill'); fill.style.width = (groups[k].length / G.counted * 100) + '%'; track.appendChild(fill); row.appendChild(track);
          row.appendChild(el('span', 'sig-bar__n', String(groups[k].length)));
          row.setAttribute('aria-label', k + ': ' + groups[k].length + ' of ' + G.counted + (groups[k].length ? ', ' + listNames(groups[k].map(b => b.name)) : ''));
          wrap.appendChild(row);
        });
        return wrap;
      };
      const cols = el('div', 'sig-cols');
      cols.appendChild(bars('Element', G.elements, ['Fire', 'Earth', 'Air', 'Water']));
      cols.appendChild(bars('Modality', G.modalities, ['Cardinal', 'Fixed', 'Mutable']));
      S.body.appendChild(cols);
      // the columns in words: where the weight is and where there is none
      const sentence = (groups, order) => {
        const sorted = order.slice().sort((a, b) => groups[b].length - groups[a].length);
        const top = sorted[0], bits = [cap(top.toLowerCase()) + ' ' + numWord(groups[top].length) + ': ' + listNames(groups[top].map(b => b.name)) + '.'];
        order.forEach(k => { if (groups[k].length === 0) bits.push('No planet in ' + k.toLowerCase() + '.'); else if (groups[k].length === 1 && k !== top) bits.push(cap(k.toLowerCase()) + ' one: ' + groups[k][0].name + ', the only ' + k.toLowerCase() + ' in the chart.'); });
        return bits.join(' ');
      };
      S.body.appendChild(el('p', 'sig-words', sentence(G.elements, ['Fire', 'Earth', 'Air', 'Water']) + ' ' + sentence(G.modalities, ['Cardinal', 'Fixed', 'Mutable'])));
      S.body.appendChild(el('p', 'an__sub', 'Three structural facts'));
      const facts = el('div', 'sig-facts');
      const fact = (title, text) => { const d = el('div', 'sig-fact'); d.appendChild(el('h4', null, title)); d.appendChild(el('p', null, text)); facts.appendChild(d); };
      const un = G.unaspected;
      fact(un.length ? (un.length === 1 ? 'An unaspected planet' : 'Unaspected planets') : 'No unaspected planet',
        // the house is only nameable when there are cusps: a chart with no birth
        // time, or one caught mid-change, has none, and houseOf(null) threw here
        un.length ? un.map(b => b.name + ', ' + posText(b.lon) + (chart.cusps ? ', ' + ordWord(houseOf(chart.cusps, b.lon)) + ' house' : '')).join('; ') + '. ' + (un.length === 1 ? 'It makes' : 'They make') + ' no aspect at all inside eight degrees. An unaspected planet is read as operating on its own terms, unmoderated by anything else in the chart.'
          : 'Every planet makes at least one aspect inside eight degrees. Nothing in this chart operates alone; each part is moderated by another.');
      const emptyQ = G.quadrants ? G.quadrants.filter(q => !q.planets.length) : [], fullQ = G.quadrants ? G.quadrants.filter(q => q.planets.length) : [];
      if (!G.quadrants) fact('The four quarters', 'Add your birthplace to see which quarters of the chart hold planets and which stand empty. The quarters are cut by the horizon and the line straight overhead.');
      else fact(emptyQ.length ? (emptyQ.length === 1 ? 'An empty quarter' : 'Empty quarters') : 'No empty quarter',
        emptyQ.length ? emptyQ.map(q => 'Houses ' + listNames(q.houses.map(numWord)) + ' hold no planets between them').join('; ') + '. The other ' + (fullQ.length === 1 ? 'quadrant holds ' : 'quadrants hold ') + listNames(fullQ.map(q => numWord(q.planets.length))) + '. The unoccupied stretch is ' + listNames(emptyQ.map(q => QUADRANT_WORDS[q.n - 1])) + '.'
          : 'Every quarter of the chart holds at least one planet: ' + G.quadrants.map(q => numWord(q.planets.length)).join(', ') + ' going round from the Ascendant. The weight is spread rather than gathered.');
      fact('The most-aspected body', G.most.name + ', with ' + numWord(G.mostCount) + ' aspects' + (G.runnerUp ? (G.runnerUpCount === G.mostCount ? ', level with ' + G.runnerUp.name : ', more than ' + G.runnerUp.name + '\'s ' + numWord(G.runnerUpCount)) : '') + '. A heavily aspected planet acts as a junction: little in the chart resolves without passing through it.');
      S.body.appendChild(facts);
    }
    // near a boundary: after the placements, whatever the stage allows
    const near = $('#near-cusp');
    if (near) {
      const flags = A ? A.nearCusp : [];
      near.hidden = !flags.length;
      near.textContent = flags.length ? 'Near a boundary. ' + flags.map(n => n.kind === 'sign'
        ? n.body.name + ' is ' + dm(n.dist) + (n.side === 'after' ? ' into ' + n.to + ' from ' + n.from : ' short of ' + n.to + ', still in ' + n.from) + '. A small error in the birth time could put it either side.'
        : n.body.name + ' sits ' + dm(n.dist) + (n.side === 'after' ? ' inside the ' + ordWord(n.to) + ' house, just past the line from the ' + ordWord(n.from) : ' short of the ' + ordWord(n.to) + ' house, still in the ' + ordWord(n.from)) + '. A birth time that is a little off could place it either side.').join(' ') : '';
    }
  }
  // an entry from the library with the chart's fields filled: the same path the readings take
  const COMPOSE_ENTRY = (path, words, chart) => {
    const facts = window.OrreryFacts.factsFor(chart, READINGS);
    const text = path.split('.').reduce((o, k) => (o && o[k] != null ? o[k] : null), READINGS);
    if (typeof text === 'string' && text.trim()) return { text: window.OrreryFacts.fill(text.trim(), facts, null), ph: false };
    return { text: '[' + path + ' · ' + words + ' words]', ph: true };
  };

  /* The wheel only redraws once a date or time has been entered and the field
     left. While either is being edited the last complete chart stays up, so
     the example (or your own chart) never changes mid-edit. */
  const committed = { dob: '', tob: '' };
  const plausibleDate = v => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    const y = Number(v.slice(0, 4));
    return y >= 1900 && y <= new Date().getFullYear() && new Date(v) <= new Date();
  };
  function commitBirth(settled) {
    const dob = form.elements.dob.value, tob = form.elements.tob.value;
    let changed = false;
    if (plausibleDate(dob) && dob !== committed.dob) { committed.dob = dob; changed = true; }
    // a time counts once it is complete; leaving it empty holds the Moon, houses and angles
    if ((tob || settled) && tob !== committed.tob) { committed.tob = tob; changed = true; }
    return changed;
  }

  // the given chart, whole: the engine's own no-time convention (sunrise, solar houses) when the time is blank
  function givenChart(dob, tob, loc) {
    const chart = loc ? ENGINE.chartFor(dob, tob || '', loc) : null;
    if (!chart) return { stage: 0, chart: null, missing: [] };
    chart.stage = 3; chart.moonKnown = true; chart.anglesKnown = !chart.solarHouses; chart.zoneAssumed = false;
    return { stage: 3, chart, missing: [] };
  }
  function updatePreview() {
    if (!wheel) return;
    const name = (form.elements.name.value || '').trim();
    const dob = committed.dob;
    const tob = committed.tob;
    const loc = placeLoc();
    const place = loc ? loc.label : (form.elements.place.value || '').trim();
    // what these inputs make honestly computable: nothing, the planets by sign, or the whole chart
    const st = GIVEN ? givenChart(dob, tob, loc) : ANALYSIS.stageFor(dob, tob, loc);
    const chart = st.chart;
    current.stage = st.stage; current.chart = chart; current.missing = st.missing;
    current.analysis = chart ? ANALYSIS.analyse(chart) : null;

    // the plate carries the name exactly as typed; the print is the same element
    const plateName = name || 'Your name';
    previewName.textContent = plateName;
    const bits = plateBits(chart, place);
    previewMeta.textContent = bits.length ? bits.join(' \u00b7 ') : 'Enter your details and the chart draws itself';
    if (previewQuote) previewQuote.textContent = quoteText();

    const key = JSON.stringify([dob, tob, loc]);
    if (key !== updatePreview.lastKey) {
      updatePreview.lastKey = key;
      drawWheel(chart);
      renderHeroPrint();   // the hero print follows the same committed change, from the same renderer
      if (typeof refreshQuoteOptions === 'function') refreshQuoteOptions();
      renderPlacements(chart);
      renderAnalysis(chart, current.analysis);
      renderSectionMine();      // each section's own reading, and the tick that chooses it
      renderContents(chart);
    }
    renderHeroPlate();
    renderHook();
    syncChartState();
  }

  window.OrreryReading = {
    page: () => current.stage === 3 ? asText(pageReading(current.chart)) : 'The reading needs a date, time and place.',
    full: () => current.stage === 3 ? asText(fullReading(current.chart)) : 'The reading needs a date, time and place.'
  };

  let previewTimer = null;
  // the print's name line carries the place while the name is still unasked, so it
  // follows the place field as it is typed, not only once a suggestion is taken
  form.elements.place.addEventListener('input', renderHeroPlate);
  form.elements.name.addEventListener('input', () => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(updatePreview, 140);
  });
  /* ---- the line the chart offers
     Drawn from the same library the reading is: OrreryCompose.quoteLines walks
     the rising sign, the Sun, the Moon and the blend and returns only whole
     sentences that are about a person, never a fragment and never an entry that
     is not written. Nothing is invented here and nothing is a placeholder — if
     the library has nothing that fits, there is no quote, and the print carries
     none.

     `fits` is measured, not counted. Both plates are set white-space: nowrap,
     so a line that would wrap makes scrollWidth exceed clientWidth; a candidate
     that fails on either the hero print or the poster is dropped rather than
     shortened. That is what keeps it to one line for any input, including the
     runs of capitals no character count can protect against. */
  const quoteEl = form.elements.quote, quoteCount = $('#quote-count');
  const quoteTools = $('#quote-tools'), quoteOffer = $('#quote-offer'), quoteOfferLine = $('#quote-offer-line');
  const quoteAdd = $('#quote-add'), quoteRemove = $('#quote-remove'), quoteNext = $('#quote-next'), quoteEdit = $('#quote-edit');
  const QUOTE_MAX = 60, QUOTE_WARN = 45;
  let quoteOptions = [], quoteIdx = 0, quoteOwned = false, quoteEditing = false, quoteLast = '';

  /* Fit is measured, never counted. Both plates are white-space: nowrap, so a
     line that would wrap makes scrollWidth exceed clientWidth. A candidate that
     fails on either plate is dropped rather than shortened, and a keystroke that
     would overflow is refused. That is what holds one line for any input. */
  function fitsOnPrint(text) {
    if (!text) return true;
    if (text.length > QUOTE_MAX) return false;
    const probe = el => {
      if (!el || !el.clientWidth) return true;
      const prev = el.textContent;
      el.textContent = text;
      const ok = el.scrollWidth <= el.clientWidth;
      el.textContent = prev;
      return ok;
    };
    return probe(previewQuote) && probe(heroPlateQuote);
  }

  const applied = () => quoteText();                       // what is on the print and on the order
  const offered = () => quoteOptions[quoteIdx] || '';

  function paintQuote() {
    const on = !!applied(), line = quoteEditing ? applied() : (on && !quoteOwned ? applied() : offered());
    if (quoteOfferLine) quoteOfferLine.textContent = line;
    if (quoteOffer) quoteOffer.hidden = quoteEditing || !line;
    if (quoteEl) quoteEl.hidden = !quoteEditing;
    if (quoteTools) quoteTools.hidden = !(quoteOptions.length || on || quoteEditing);
    // one button at a time: add it, or take it off again
    if (quoteAdd) quoteAdd.hidden = on || quoteEditing || !offered();
    if (quoteRemove) quoteRemove.hidden = !on;
    if (quoteNext) quoteNext.hidden = quoteEditing || quoteOptions.length < 2;
    if (quoteEdit) { quoteEdit.hidden = quoteEditing; quoteEdit.textContent = on ? 'Edit' : 'Write my own'; }
    if (quoteCount) {
      const left = QUOTE_MAX - quoteEl.value.length;
      quoteCount.textContent = left === 1 ? '1 left' : left + ' left';
      quoteCount.hidden = !quoteEditing || applied().length < QUOTE_WARN;
      quoteCount.classList.toggle('is-full', left <= 0);
    }
    renderHeroPlate();
    if (previewQuote) previewQuote.textContent = applied();
  }

  function setApplied(text) {
    if (!quoteEl) return;
    quoteEl.value = text;
    quoteLast = text;
    paintQuote();
  }

  /* A new set of birth details is a new chart and a new line. Their own wording
     is never overwritten. A line they added stays added and is replaced by the
     new chart's; a line they removed stays off. */
  function refreshQuoteOptions() {
    if (!quoteEl || !COMPOSE.quoteLines) return;
    const chart = current.stage === 3 ? current.chart : null;
    const wasOn = !!applied() && !quoteOwned;
    quoteOptions = chart ? COMPOSE.quoteLines(chart, READINGS, fitsOnPrint).slice(0, 3) : [];
    quoteIdx = 0;
    if (quoteOwned) { paintQuote(); return; }
    setApplied(wasOn ? (offered() || '') : '');
  }

  if (quoteEl) {
    quoteEl.addEventListener('input', () => {
      if (!fitsOnPrint(quoteText())) { quoteEl.value = quoteLast; return; }
      quoteLast = quoteEl.value;
      quoteOwned = true;
      paintQuote();
    });
  }
  // one tap puts it on the print: no confirming, no typing
  if (quoteAdd) quoteAdd.addEventListener('click', () => { quoteOwned = false; setApplied(offered()); });
  if (quoteRemove) quoteRemove.addEventListener('click', () => { quoteOwned = false; quoteEditing = false; setApplied(''); });
  if (quoteNext) quoteNext.addEventListener('click', () => {
    if (quoteOptions.length < 2) return;
    quoteIdx = (quoteIdx + 1) % quoteOptions.length;
    quoteOwned = false;
    // added, it swaps on the print; not yet added, it just changes what is offered
    if (applied()) setApplied(offered()); else paintQuote();
  });
  if (quoteEdit) quoteEdit.addEventListener('click', () => {
    quoteEditing = true;
    if (!applied() && offered()) { quoteEl.value = offered(); quoteLast = quoteEl.value; }
    paintQuote();
    quoteEl.focus({ preventScroll: true });
  });
  // the date is taken when the field is left, not while its day, month and
  // year are being changed one by one; the time likewise
  ['dob', 'tob'].forEach(id => {
    const input = form.elements[id];
    if (!input) return;
    const settle = () => { if (commitBirth(true)) { updatePreview(); renderPlaceNote(); } };
    input.addEventListener('blur', settle);
    // a native picker on a phone can commit without the field losing focus
    input.addEventListener('change', () => { if (document.activeElement !== input) settle(); });
  });

  /* ----------------------------------------------------------- place of birth
     Typed text is matched against a gazetteer as it is entered. Picking a
     match (or leaving the field with matches showing) stores the coordinates
     and IANA time zone in hidden inputs, which the preview, the draft and the
     order all carry. Without a match the order still goes through: the proof
     confirms the place. */
  const placeInput = form.elements.place;
  const placeList = $('#place-list');
  const placeNote = $('#place-note');
  const placeStore = { label: form.elements.place_label, lat: form.elements.place_lat, lon: form.elements.place_lon, tz: form.elements.place_tz };
  const PLACE_API = 'https://geocoding-api.open-meteo.com/v1/search?count=6&language=en&format=json&name=';
  const placeCache = {};
  let placeMatches = [], placeActive = -1, placeTimer = null, placeSeq = 0, placeFailed = false;

  function placeLoc() {
    if (!placeStore.tz || !placeStore.tz.value) return null;
    return { label: placeStore.label.value, lat: Number(placeStore.lat.value), lon: Number(placeStore.lon.value), tz: placeStore.tz.value };
  }
  function placeLabel(r) {
    return [r.name, r.admin1, r.country].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ');
  }
  function zoneWords(tz, dob) {
    // "Eastern Daylight Time (UTC−4)" for the zone on the birth date, if there is one
    try {
      const p = (dob || '').split('-');
      const y = Number(p[0]) || 2000, mo = Number(p[1]) || 1, d = Number(p[2]) || 1;
      const off = tzOffsetMinutes(tz, y, mo, d, 12, 0);
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' }).formatToParts(new Date(Date.UTC(y, mo - 1, d, 12 - (off || 0) / 60)));
      const nm = (parts.find(x => x.type === 'timeZoneName') || {}).value || tz;
      if (off == null) return nm;
      const sign = off < 0 ? '\u2212' : '+';
      const h = Math.floor(Math.abs(off) / 60), m = Math.abs(off) % 60;
      return nm + ' (UTC' + sign + h + (m ? ':' + String(m).padStart(2, '0') : '') + ')';
    } catch (_) { return tz; }
  }
  function renderPlaceNote() {
    if (!placeNote) return;
    const loc = placeLoc();
    const typed = (placeInput.value || '').trim();
    placeNote.classList.toggle('is-set', !!loc);
    if (loc) {
      // the place is already in the field above and on the plate under the chart;
      // the only thing this line knows that they do not is which clock was used
      placeNote.textContent = zoneWords(loc.tz, committed.dob);
    } else if (typed.length >= 2 && placeFailed) {
      placeNote.textContent = 'We could not look that up just now. Carry on; we confirm the place and time zone on your proof.';
    } else if (typed.length >= 2) {
      placeNote.textContent = 'Pick the town from the list so the chart uses its time zone.';
    } else {
      placeNote.textContent = '';
    }
  }
  function setPlace(r) {
    placeStore.label.value = r ? placeLabel(r) : '';
    placeStore.lat.value = r ? Number(r.latitude).toFixed(4) : '';
    placeStore.lon.value = r ? Number(r.longitude).toFixed(4) : '';
    placeStore.tz.value = r ? r.timezone : '';
    if (r) placeInput.value = placeLabel(r);
    renderPlaceNote();
    saveDraft();
    updatePreview();
  }
  function closePlaceList() {
    placeList.hidden = true;
    placeInput.setAttribute('aria-expanded', 'false');
    placeInput.removeAttribute('aria-activedescendant');
    placeActive = -1;
  }
  function paintPlaceList() {
    while (placeList.firstChild) placeList.removeChild(placeList.firstChild);
    placeMatches.forEach((r, i) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.id = 'place-opt-' + i;
      li.setAttribute('aria-selected', i === placeActive ? 'true' : 'false');
      li.className = i === placeActive ? 'is-active' : '';
      li.textContent = r.name;
      const sub = document.createElement('span');
      sub.className = 'place-list__sub';
      sub.textContent = [r.admin1, r.country].filter(Boolean).join(', ');
      li.appendChild(sub);
      li.addEventListener('mousedown', ev => { ev.preventDefault(); setPlace(r); closePlaceList(); });
      placeList.appendChild(li);
    });
    if (placeActive >= 0) placeInput.setAttribute('aria-activedescendant', 'place-opt-' + placeActive);
    else placeInput.removeAttribute('aria-activedescendant');
  }
  function showPlaceStatus(text) {
    while (placeList.firstChild) placeList.removeChild(placeList.firstChild);
    const li = document.createElement('li');
    li.className = 'place-list__status';
    li.textContent = text;
    placeList.appendChild(li);
    placeList.hidden = false;
    placeInput.setAttribute('aria-expanded', 'true');
  }
  function rankPlaces(results, hint) {
    // "Savannah, Georgia": whatever follows the comma pulls matching regions to the top
    const h = (hint || '').trim().toLowerCase();
    if (!h) return results;
    const score = r => {
      const fields = [r.admin1, r.country, r.admin2, r.country_code].filter(Boolean).map(v => String(v).toLowerCase());
      if (fields.some(v => v === h)) return 2;
      if (fields.some(v => v.startsWith(h) || h.startsWith(v))) return 1;
      return 0;
    };
    return results.map((r, i) => ({ r: r, s: score(r), i: i })).sort((a, b) => b.s - a.s || a.i - b.i).map(x => x.r);
  }
  function fetchPlaces(q) {
    if (placeCache[q]) return Promise.resolve(placeCache[q]);
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    if (fetchPlaces.ctrl) fetchPlaces.ctrl.abort();
    fetchPlaces.ctrl = ctrl;
    return fetch(PLACE_API + encodeURIComponent(q), ctrl ? { signal: ctrl.signal } : undefined)
      .then(res => { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
      .then(data => (placeCache[q] = (data.results || []).filter(r => r.timezone && isFinite(r.latitude))));
  }
  function lookupPlace(auto) {
    const raw = (placeInput.value || '').trim();
    const comma = raw.indexOf(',');
    const head = (comma >= 0 ? raw.slice(0, comma) : raw).trim();
    const hint = comma >= 0 ? raw.slice(comma + 1) : '';
    if (head.length < 2) { placeMatches = []; closePlaceList(); renderPlaceNote(); return; }
    const seq = ++placeSeq;
    fetchPlaces(head).then(results => {
      if (seq !== placeSeq) return;
      placeFailed = false;
      placeMatches = rankPlaces(results, hint);
      placeActive = -1;
      const focused = document.activeElement === placeInput;
      if (!placeMatches.length) {
        if (focused && !auto) showPlaceStatus('No match. Try the nearest larger town.');
        renderPlaceNote();
        return;
      }
      if (focused && !auto) {
        paintPlaceList();
        placeList.hidden = false;
        placeInput.setAttribute('aria-expanded', 'true');
      } else if (!placeLoc()) {
        // the field was left while a lookup was still out: take the best match
        setPlace(placeMatches[0]);
      }
    }).catch(err => {
      if (err && err.name === 'AbortError') return;
      if (seq !== placeSeq) return;
      placeFailed = true;
      placeMatches = [];
      closePlaceList();
      renderPlaceNote();
    });
  }
  if (placeInput && placeList) {
    placeInput.addEventListener('input', () => {
      // any edit un-sets the stored place until a match is chosen again
      if (placeStore.tz.value && placeInput.value.trim() !== placeStore.label.value) setPlace(null);
      renderPlaceNote();
      clearTimeout(placeTimer);
      placeTimer = setTimeout(() => lookupPlace(false), 220);
    });
    placeInput.addEventListener('focus', () => {
      if (placeMatches.length && !placeLoc()) { paintPlaceList(); placeList.hidden = false; placeInput.setAttribute('aria-expanded', 'true'); }
    });
    placeInput.addEventListener('blur', () => {
      clearTimeout(placeTimer);
      closePlaceList();
      if (!placeLoc() && placeMatches.length && placeInput.value.trim().length >= 2) setPlace(placeMatches[0]);
      else if (!placeLoc() && placeInput.value.trim().length >= 2) lookupPlace(true);
      else updatePreview();
    });
    placeInput.addEventListener('keydown', ev => {
      const open = !placeList.hidden && placeMatches.length;
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        if (!placeMatches.length) return;
        ev.preventDefault();
        if (placeList.hidden) { placeList.hidden = false; placeInput.setAttribute('aria-expanded', 'true'); }
        const n = placeMatches.length;
        placeActive = ev.key === 'ArrowDown' ? (placeActive + 1) % n : (placeActive - 1 + n) % n;
        paintPlaceList();
      } else if (ev.key === 'Enter' && open) {
        ev.preventDefault();
        setPlace(placeMatches[placeActive >= 0 ? placeActive : 0]);
        closePlaceList();
      } else if (ev.key === 'Escape' && !placeList.hidden) {
        ev.stopPropagation();
        closePlaceList();
      }
    });
    document.addEventListener('mousedown', ev => { if (!placeList.hidden && !ev.target.closest('.field--place')) closePlaceList(); });
  }
  window.addEventListener('resize', () => { if (pinned) showTip(pinned); });

  /* ----------------------------------------------------------- hero loupe */
  const loupe = $('#hero-loupe');
  if (loupe) {
    const lens = $('.loupe__lens', loupe);
    const img = $('img', loupe);
    const hoverable = matchMedia('(hover: hover)');
    const ZOOMS = [1.5, 2];
    let zi = 0, held = false, dragging = false, hires = null, hiresReady = false;
    let px = 0.5, py = 0.5;   // pointer as a fraction of the figure

    // the 4K copy is only fetched the first time someone reaches for the lens
    function loadHires() {
      if (hires) return;
      hires = new Image();
      hires.onload = () => { hiresReady = true; lens.style.backgroundImage = 'url("' + loupe.dataset.hires + '")'; render(); };
      hires.src = loupe.dataset.hires;
      lens.style.backgroundImage = 'url("' + img.currentSrc + '")';   // sharp enough until the 4K arrives
    }

    function render() {
      const r = loupe.getBoundingClientRect();
      const b = img.getBoundingClientRect();   // the img box as rendered, CSS zoom included
      const zoom = ZOOMS[zi];
      const iw = img.naturalWidth || 1168, ih = img.naturalHeight || 880;
      // object-fit: cover centres the photo in that box, so work out where it actually sits
      const sc = Math.max(b.width / iw, b.height / ih);
      const dw = iw * sc, dh = ih * sc;
      const ox = (b.left - r.left) + (b.width - dw) / 2, oy = (b.top - r.top) + (b.height - dh) / 2;
      const x = px * r.width, y = py * r.height;
      const L = lens.offsetWidth;
      lens.style.left = x.toFixed(1) + 'px';
      lens.style.top = y.toFixed(1) + 'px';
      lens.style.backgroundSize = (dw * zoom).toFixed(1) + 'px ' + (dh * zoom).toFixed(1) + 'px';
      lens.style.backgroundPosition = (-((x - ox) * zoom - L / 2)).toFixed(1) + 'px ' + (-((y - oy) * zoom - L / 2)).toFixed(1) + 'px';
    }
    const setPoint = (clientX, clientY) => {
      const r = loupe.getBoundingClientRect();
      px = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
      py = Math.max(0, Math.min(1, (clientY - r.top) / r.height));
      render();
    };
    const activate = on => {
      if (on) loadHires();
      loupe.classList.toggle('is-active', on);
      loupe.classList.add('is-used');
    };
    const setHeld = on => { held = on; loupe.classList.toggle('is-held', on); activate(on); };

    // mouse and pen: the lens follows while the pointer is over the photo
    loupe.addEventListener('pointerenter', e => {
      if (e.pointerType === 'touch') return;
      setPoint(e.clientX, e.clientY);
      activate(true);
    });
    loupe.addEventListener('pointermove', e => {
      if (e.pointerType === 'touch') { if (dragging) setPoint(e.clientX, e.clientY); return; }
      setPoint(e.clientX, e.clientY);
    });
    loupe.addEventListener('pointerleave', e => {
      if (e.pointerType === 'touch' || held) return;
      activate(false);
    });

    // touch: press to look, drag to move, lift to release; a tap holds it in place
    loupe.addEventListener('pointerdown', e => {
      if (e.pointerType !== 'touch') return;
      dragging = true;
      setPoint(e.clientX, e.clientY);
      activate(true);
      loupe.setPointerCapture(e.pointerId);
    });
    const touchEnd = e => {
      if (e.pointerType !== 'touch') return;
      dragging = false;
      if (!held) activate(false);
    };
    loupe.addEventListener('pointerup', touchEnd);
    loupe.addEventListener('pointercancel', touchEnd);
    loupe.addEventListener('click', e => {
      if (hoverable.matches && e.detail) {
        // mouse: click steps the magnification
        zi = (zi + 1) % ZOOMS.length;
        render();
        return;
      }
      if (e.detail) { setPoint(e.clientX, e.clientY); setHeld(!held); }
    });

    // keyboard: Enter toggles, arrows move, Escape releases
    loupe.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHeld(!held); render(); }
      else if (held && /^Arrow(Left|Right|Up|Down)$/.test(e.key)) {
        e.preventDefault();
        if (e.key === 'ArrowLeft') px = Math.max(0, px - 0.05);
        if (e.key === 'ArrowRight') px = Math.min(1, px + 0.05);
        if (e.key === 'ArrowUp') py = Math.max(0, py - 0.05);
        if (e.key === 'ArrowDown') py = Math.min(1, py + 0.05);
        render();
      } else if (held && (e.key === '+' || e.key === '=' || e.key === '-')) {
        e.preventDefault(); zi = (zi + 1) % ZOOMS.length; render();
      } else if (e.key === 'Escape' && held) setHeld(false);
    });
    loupe.addEventListener('blur', () => { if (held && hoverable.matches) setHeld(false); });
    window.addEventListener('resize', () => { if (loupe.classList.contains('is-active')) render(); });
  }

  /* ----------------------------------------------------------- init */
  // The page opens on an empty wheel, or on this visitor's own chart if their
  // draft already holds a date. There is no example: nothing here describes anyone else.
  commitBirth(true);
  renderPlaceNote();
  syncFormat();
  updatePreview();
  openFromLink();
  openFromChartId();

  window.orreryTrack('ViewContent', 'view_item', {
    content_type: 'product',
    content_ids: ['orrery-birth-chart'],
    content_name: 'Orrery birth chart',
    currency: CURRENCY,
    value: currentPrice()
  }, {
    currency: CURRENCY,
    value: currentPrice(),
    items: [{ item_id: 'orrery-birth-chart', item_name: 'Orrery birth chart', price: currentPrice(), quantity: 1 }]
  });
})();

